import { DestinationHealthInfo } from "@/types";
import { useState, useEffect } from "react";
import {
  medicationService,
  type AIRecommendationsResponse,
} from "@/services/medications";
import {
  MedicationSelectionModal,
  ManualSelection,
} from "./MedicationSelectionModal";
import {
  pharmacyService,
  VaccineProtocol,
  MedicationProtocol,
} from "@/services/pharmacy";
import { ADMINISTRATION_SITES, type AdministrationSite } from "@/services/travelers";
import { RefillsDropdown } from "./RefillsDropdown";
import { RecordAdministrationModal } from "./RecordAdministrationModal";

interface MedicationItem {
  id: string;
  name: string;
  disease: string;
  riskLevel: string;
}

interface ManualSelectionPayload {
  item_type: "vaccine" | "medication";
  item_key: string;
  action: "select" | "deselect";
  note?: string;
  refill_info?: string;
  administration_site?: AdministrationSite;
  quantity?: string;
  source?: 'pharmacist_added' | 'not_recommended';
}

interface MedicationChecklistSectionProps {
  destinationInfoMap: Record<string, DestinationHealthInfo>;
  medicationSelections: Record<string, boolean | null>;
  onSelectionChange: (
    medicationId: string,
    value: boolean | null,
    noteOverride?: string,
  ) => void;
  medicationNotes: Record<string, string>;
  onNoteChange: (medicationId: string, note: string) => void;
  onSaveNote?: (medicationId: string, note: string) => Promise<boolean>;
  refillInfos?: Record<string, string>;
  onRefillInfoChange?: (medicationId: string, refillInfo: string) => void;
  administrationSites?: Record<string, AdministrationSite | null>;
  onAdministrationSiteChange?: (medicationId: string, site: AdministrationSite | null) => void;
  quantities?: Record<string, string>;
  onQuantityChange?: (medicationId: string, quantity: string) => void;
  onSaveDetails?: (
    medicationId: string,
    note: string,
    refillInfo: string,
    administrationSite: AdministrationSite | null,
    quantity?: string
  ) => Promise<boolean>;
  isLoading?: boolean;
  travelerId: string;
  aiRecommendations?: AIRecommendationsResponse | null;
  onSaveSelections?: (
    manualSelections?: ManualSelectionPayload[],
  ) => Promise<void>;
  savingSelections?: boolean;
  onRefresh?: () => Promise<void>;
  pharmacyActions?: any[];
  selections?: Array<{
    item_type: string;
    item_key: string;
    action: string;
    source: string;
    note: string | null;
    selected_by: string;
  }>;
  pharmacistRecommendationNote?: string;
  onRecommendationNoteChange?: (note: string) => void;
}

export function MedicationChecklistSection({
  destinationInfoMap,
  medicationSelections,
  onSelectionChange,
  medicationNotes,
  onNoteChange,
  onSaveNote,
  refillInfos = {},
  onRefillInfoChange,
  administrationSites = {},
  onAdministrationSiteChange,
  quantities = {},
  onQuantityChange,
  onSaveDetails,
  isLoading = false,
  travelerId,
  aiRecommendations: externalAiRecommendations,
  onSaveSelections,
  savingSelections = false,
  onRefresh,
  selections,
  pharmacistRecommendationNote = "",
  onRecommendationNoteChange,
}: MedicationChecklistSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [aiRecommendations, setAiRecommendations] =
    useState<AIRecommendationsResponse | null>(
      externalAiRecommendations || null,
    );
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [recommendationsError, setRecommendationsError] = useState<
    string | null
  >(null);
  const [vaccinesExpanded, setVaccinesExpanded] = useState(false);
  const [medicationsExpanded, setMedicationsExpanded] = useState(false);
  const [notRecommendedExpanded, setNotRecommendedExpanded] = useState(false);
  const [expandedDetails, setExpandedDetails] = useState<
    Record<string, boolean>
  >({});
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>(
    {},
  );
  const [manualSelections, setManualSelections] = useState<ManualSelection[]>(
    [],
  );

  // Vaccine administration recording (CAIR) modal target
  const [recordAdminTarget, setRecordAdminTarget] = useState<{
    protocolKey: string;
    vaccineName?: string;
  } | null>(null);

  // Track which manual selections have been saved to API
  const [savedManualSelectionKeys, setSavedManualSelectionKeys] = useState<
    Set<string>
  >(new Set());

  // Track which notes are currently being saved
  const [savingNotes, setSavingNotes] = useState<Record<string, boolean>>({});

  // Protocol maps for looking up dosage/route info
  const [vaccineProtocolMap, setVaccineProtocolMap] = useState<
    Record<string, VaccineProtocol>
  >({});
  const [medicationProtocolMap, setMedicationProtocolMap] = useState<
    Record<string, MedicationProtocol>
  >({});

  const toggleDetails = (id: string) => {
    setExpandedDetails((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const toggleCard = (id: string) => {
    setExpandedCards((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Helper function to save a note with loading state
  const handleSaveNote = async (medicationId: string) => {
    if (!onSaveNote) {
      console.error("onSaveNote is not defined - cannot save note");
      return;
    }

    const note = medicationNotes[medicationId] || "";

    setSavingNotes((prev) => ({ ...prev, [medicationId]: true }));
    try {
      const result = await onSaveNote(medicationId, note);
    } catch (error) {
      console.error("Error in handleSaveNote:", error);
    } finally {
      setSavingNotes((prev) => ({ ...prev, [medicationId]: false }));
    }
  };

  // Open the record-administration modal for a vaccine row.
  // RefillsDropdown ids are prefixed (manual-/ai-/not-rec-vaccine-<protocolKey>).
  const handleRecordAdministration = (medicationId: string) => {
    const protocolKey = medicationId.replace(
      /^(manual|ai|not-rec)-vaccine-/,
      "",
    );
    setRecordAdminTarget({
      protocolKey,
      vaccineName: vaccineProtocolMap[protocolKey]?.displayName,
    });
  };

  // Helper function to handle manual selection changes and save to API
  const handleManualSelectionChange = async (
    protocolId: string,
    itemType: "vaccine" | "medication",
    value: boolean,
    note?: string,
  ) => {
    const selectionKey = `manual-${itemType}-${protocolId}`;

    // Update local state
    onSelectionChange(selectionKey, value);

    // Save to API
    if (onSaveSelections) {
      const payload: ManualSelectionPayload = {
        item_type: itemType,
        item_key: protocolId,
        action: value ? "select" : "deselect",
      };

      if (note) {
        payload.note = note;
      }

      try {
        await onSaveSelections([payload]);
      } catch (error) {
        console.error("Failed to save manual selection:", error);
      }
    }
  };

  // Helper function to filter vaccines that should be shown
  // Includes RECOMMENDED, CONDITIONAL, and CONTRAINDICATED vaccines that are required for entry
  const shouldShowVaccine = (vaccine: any) => {
    if (vaccine.status === "RECOMMENDED" || vaccine.status === "CONDITIONAL") {
      return true;
    }
    // Show contraindicated vaccines ONLY if required for entry
    if (vaccine.status === "CONTRAINDICATED") {
      return vaccine.flags?.includes("REQUIRED_FOR_ENTRY");
    }
    return false;
  };

  useEffect(() => {
    const fetchRecommendations = async () => {
      if (!travelerId) return;
      // Skip fetching if recommendations were passed in
      if (externalAiRecommendations) {
        setAiRecommendations(externalAiRecommendations);
        return;
      }

      setLoadingRecommendations(true);
      setRecommendationsError(null);
      try {
        const data = await medicationService.getAIRecommendations(travelerId);
        setAiRecommendations(data);
      } catch (error) {
        setRecommendationsError("Failed to load AI recommendations");
      } finally {
        setLoadingRecommendations(false);
      }
    };

    fetchRecommendations();
  }, [travelerId, externalAiRecommendations]);

  // Load manual selections from recommendation API response
  useEffect(() => {
    if (!aiRecommendations?.recommendation) {
      return;
    }

    const loadManualSelections = async () => {
      try {
        const recommendation = aiRecommendations.recommendation;
        const pharmacistAddedVaccines =
          recommendation.pharmacistAddedVaccines || [];
        const pharmacistAddedMedications =
          recommendation.pharmacistAddedMedications || [];

        if (
          pharmacistAddedVaccines.length === 0 &&
          pharmacistAddedMedications.length === 0
        ) {
          return;
        }

        const protocols = await pharmacyService.getProtocols();

        const loadedManualSelections: ManualSelection[] = [];
        const savedKeys = new Set<string>();

        // Process pharmacist-added vaccines
        for (const addedItem of pharmacistAddedVaccines) {
          let protocol = protocols.protocols.vaccines.find(
            (v) => v.protocolId === addedItem.protocol_id,
          );

          if (!protocol && addedItem.protocolMetadata) {
            protocol = {
              protocolId: addedItem.protocol_id,
              displayName: addedItem.name,
              therapyClass: addedItem.therapy_class,
              ...addedItem.protocolMetadata,
            } as any;
          }

          if (protocol) {
            loadedManualSelections.push({
              type: "vaccine",
              protocol: protocol,
              note: addedItem.selectionDetails?.pharmacistNote || "",
            });

            const noteKey = `manual-vaccine-${addedItem.protocol_id}`;
            savedKeys.add(noteKey);

            if (addedItem.selectionDetails?.pharmacistNote) {
              onNoteChange(noteKey, addedItem.selectionDetails.pharmacistNote);
            }

            // Only update selection if it's different from current state to avoid loops
            const currentSelection = medicationSelections[noteKey];
            if (currentSelection !== addedItem.isSelected) {
              onSelectionChange(noteKey, addedItem.isSelected);
            }
          }
        }

        // Process pharmacist-added medications
        for (const addedItem of pharmacistAddedMedications) {
          let protocol = protocols.protocols.medications.find(
            (m) => m.protocolId === addedItem.protocol_id,
          );

          if (!protocol && addedItem.protocolMetadata) {
            protocol = {
              protocolId: addedItem.protocol_id,
              displayName: addedItem.name,
              therapyClass: addedItem.therapy_class,
              ...addedItem.protocolMetadata,
            } as any;
          }

          if (protocol) {
            loadedManualSelections.push({
              type: "medication",
              protocol: protocol,
              note: addedItem.selectionDetails?.pharmacistNote || "",
            });

            const noteKey = `manual-medication-${addedItem.protocol_id}`;
            savedKeys.add(noteKey);

            if (addedItem.selectionDetails?.pharmacistNote) {
              onNoteChange(noteKey, addedItem.selectionDetails.pharmacistNote);
            }

            // Only update selection if it's different from current state to avoid loops
            const currentSelection = medicationSelections[noteKey];
            if (currentSelection !== addedItem.isSelected) {
              onSelectionChange(noteKey, addedItem.isSelected);
            }
          }
        }

        setManualSelections((prevManual) => {
          const existingLocalKeys = prevManual.filter((sel) => {
            const key = `manual-${sel.type}-${sel.protocol.protocolId}`;
            return !savedKeys.has(key);
          });

          return [...loadedManualSelections, ...existingLocalKeys];
        });

        setSavedManualSelectionKeys(savedKeys);
      } catch (error) {
        // Silent fail
      }
    };

    loadManualSelections();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiRecommendations]);

  // Fetch protocols to get dosage/route info for NOT_RECOMMENDED items
  useEffect(() => {
    const fetchProtocols = async () => {
      try {
        const protocols = await pharmacyService.getProtocols();

        // Build vaccine protocol map
        const vaccineMap: Record<string, VaccineProtocol> = {};
        protocols.protocols.vaccines.forEach((vaccine) => {
          vaccineMap[vaccine.protocolId] = vaccine;
        });
        setVaccineProtocolMap(vaccineMap);

        // Build medication protocol map
        const medMap: Record<string, MedicationProtocol> = {};
        protocols.protocols.medications.forEach((med) => {
          medMap[med.protocolId] = med;
        });
        setMedicationProtocolMap(medMap);
      } catch (error) {
        // Silent fail - protocol info is optional enhancement
      }
    };

    fetchProtocols();
  }, []);

  // Extract all recommended medications from destination health info
  const getMedicationsFromDestinations = (): MedicationItem[] => {
    const medications: MedicationItem[] = [];
    const seenMeds = new Set<string>();

    Object.entries(destinationInfoMap).forEach(([country, info]) => {
      const healthRecs = info?.health_recommendations;
      if (!healthRecs) return;

      // Get regular medications (malaria prophylaxis, etc.)
      healthRecs.medications?.forEach((med: any) => {
        med.medications?.prophylaxis_drugs?.forEach((drug: string) => {
          const normalizedDrug = drug.toLowerCase().replace(/-/g, " ");
          if (!seenMeds.has(normalizedDrug)) {
            seenMeds.add(normalizedDrug);
            medications.push({
              id: `${med.disease_code}-${drug}`,
              name: drug
                .replace(/-/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase()),
              disease: med.disease,
              riskLevel: med.risk_level,
            });
          }
        });
      });

      // Get conditional medications
      healthRecs.conditional_medications?.forEach((med: any) => {
        med.medications?.prophylaxis_drugs?.forEach((drug: string) => {
          const normalizedDrug = drug.toLowerCase().replace(/-/g, " ");
          if (!seenMeds.has(normalizedDrug)) {
            seenMeds.add(normalizedDrug);
            medications.push({
              id: `${med.disease_code}-${drug}`,
              name: drug
                .replace(/-/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase()),
              disease: med.disease,
              riskLevel: med.risk_level,
            });
          }
        });
      });

      // Get malaria prophylaxis drugs
      if (
        healthRecs.malaria_risk?.prophylaxis_recommended &&
        healthRecs.malaria_risk?.prophylaxis_drugs
      ) {
        healthRecs.malaria_risk.prophylaxis_drugs.forEach((drug: string) => {
          const normalizedDrug = drug.toLowerCase().replace(/-/g, " ");
          if (!seenMeds.has(normalizedDrug)) {
            seenMeds.add(normalizedDrug);
            medications.push({
              id: `malaria-${drug}`,
              name: drug
                .replace(/-/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase()),
              disease: "Malaria",
              riskLevel: healthRecs.malaria_risk?.risk_level || "high",
            });
          }
        });
      }

      // Get environmental risks that may have medications (altitude sickness, travelers diarrhea)
      const diseaseRisks = healthRecs.disease_risks;
      if (diseaseRisks?.altitude_sickness?.recommendations?.medications) {
        const altMeds =
          diseaseRisks.altitude_sickness.recommendations.medications;
        const medsArray = Array.isArray(altMeds) ? altMeds : [];
        medsArray.forEach((drug: any) => {
          const drugName = typeof drug === "string" ? drug : drug?.name || "";
          if (!drugName) return;
          const normalizedDrug = drugName.toLowerCase();
          if (!seenMeds.has(normalizedDrug)) {
            seenMeds.add(normalizedDrug);
            medications.push({
              id: `altitude-${drugName}`,
              name: drugName.replace(/\b\w/g, (l: string) => l.toUpperCase()),
              disease: "Altitude Sickness",
              riskLevel:
                diseaseRisks.altitude_sickness?.risk_level || "moderate",
            });
          }
        });
      }

      if (diseaseRisks?.travelers_diarrhea?.recommendations?.medications) {
        const tdMeds =
          diseaseRisks.travelers_diarrhea.recommendations.medications;
        const medsArray = Array.isArray(tdMeds) ? tdMeds : [];
        medsArray.forEach((drug: any) => {
          const drugName = typeof drug === "string" ? drug : drug?.name || "";
          if (!drugName) return;
          const normalizedDrug = drugName.toLowerCase();
          if (!seenMeds.has(normalizedDrug)) {
            seenMeds.add(normalizedDrug);
            medications.push({
              id: `diarrhea-${drugName}`,
              name: drugName.replace(/\b\w/g, (l: string) => l.toUpperCase()),
              disease: "Travelers Diarrhea",
              riskLevel:
                diseaseRisks.travelers_diarrhea?.risk_level || "moderate",
            });
          }
        });
      }
    });

    return medications;
  };

  const medications = getMedicationsFromDestinations();

  const getRiskLevelColor = (level: string) => {
    const colors: Record<string, { bg: string; border: string; text: string }> =
      {
        critical: { bg: "#fef2f2", border: "#dc2626", text: "#991b1b" },
        high: { bg: "#fef2f2", border: "#dc2626", text: "#991b1b" },
        essential: { bg: "#fff7ed", border: "#ea580c", text: "#9a3412" },
        important: { bg: "#fefce8", border: "#eab308", text: "#713f12" },
        moderate: { bg: "#fffbeb", border: "#f59e0b", text: "#92400e" },
        consider: { bg: "#eff6ff", border: "#2563eb", text: "#1e40af" },
        low: { bg: "#f0fdf4", border: "#16a34a", text: "#166534" },
      };
    return (
      colors[level] || { bg: "#f9fafb", border: "#6b7280", text: "#374151" }
    );
  };

  const getSelectionStyle = (value: boolean | null) => {
    if (value === true) {
      return {
        bg: "#dcfce7",
        border: "#16a34a",
        text: "#166534",
      };
    }
    if (value === false) {
      return {
        bg: "#fee2e2",
        border: "#dc2626",
        text: "#991b1b",
      };
    }
    return {
      bg: "#f3f4f6",
      border: "#d1d5db",
      text: "#6b7280",
    };
  };

  const handleManualSelectionAdd = (selection: ManualSelection) => {
    const protocolId = selection.protocol.protocolId;
    const itemType = selection.type;

    // Check if already exists in AI recommendations (vaccines or medications)
    if (itemType === "vaccine") {
      const existsInAIRecommended =
        aiRecommendations?.recommendation.vaccines.some(
          (v) => v.protocol_id === protocolId,
        );
      if (existsInAIRecommended) {
        alert("This vaccine is already in the AI recommendations list.");
        return;
      }

      // Check if exists in NOT RECOMMENDED vaccines
      const existsInNotRecommended =
        aiRecommendations?.recommendation.notRecommended?.vaccines?.some(
          (v) => v.protocol_id === protocolId,
        );
      if (existsInNotRecommended) {
        alert("This vaccine is already in the Not Recommended list.");
        return;
      }

      // Check if exists in pharmacist added vaccines
      const existsInPharmacistAdded =
        aiRecommendations?.recommendation.pharmacistAddedVaccines?.some(
          (v) => v.protocol_id === protocolId,
        );
      if (existsInPharmacistAdded) {
        alert("This vaccine has already been added by a pharmacist.");
        return;
      }
    } else if (itemType === "medication") {
      const existsInAIRecommended =
        aiRecommendations?.recommendation.medications.some(
          (m) => m.protocol_id === protocolId,
        );
      if (existsInAIRecommended) {
        alert("This medication is already in the AI recommendations list.");
        return;
      }

      // Check if exists in NOT RECOMMENDED medications
      const existsInNotRecommended =
        aiRecommendations?.recommendation.notRecommended?.medications?.some(
          (m) => m.protocol_id === protocolId,
        );
      if (existsInNotRecommended) {
        alert("This medication is already in the Not Recommended list.");
        return;
      }

      // Check if exists in pharmacist added medications
      const existsInPharmacistAdded =
        aiRecommendations?.recommendation.pharmacistAddedMedications?.some(
          (m) => m.protocol_id === protocolId,
        );
      if (existsInPharmacistAdded) {
        alert("This medication has already been added by a pharmacist.");
        return;
      }
    }

    // Check if already exists in current session manual selections
    const existsInManualSelections = manualSelections.some(
      (s) => s.protocol.protocolId === protocolId && s.type === itemType,
    );
    if (existsInManualSelections) {
      alert(`This ${itemType} has already been added in this session.`);
      return;
    }

    setManualSelections((prev) => [...prev, selection]);
  };

  const handleManualSelectionRemove = (protocolId: string) => {
    // Find the item to determine its type
    const item = manualSelections.find(
      (s) => s.protocol.protocolId === protocolId,
    );

    // Remove from local manual selections state
    setManualSelections((prev) =>
      prev.filter((s) => s.protocol.protocolId !== protocolId),
    );

    // Also set the selection to false and call API to deselect
    if (item) {
      const selectionKey = `manual-${item.type}-${protocolId}`;
      onSelectionChange(selectionKey, false);
    } else {
      // If not in manualSelections, it might be from pharmacistAddedVaccines/Medications
      // Try both vaccine and medication keys
      onSelectionChange(`manual-vaccine-${protocolId}`, false);
      onSelectionChange(`manual-medication-${protocolId}`, false);
    }
  };

  const handleSaveSelectionsWithManual = async () => {
    // Build manual selections array to pass to the combined save function
    const manualItemsToSave: ManualSelectionPayload[] = [];

    manualSelections.forEach((sel) => {
      const noteKey = `manual-${sel.type}-${sel.protocol.protocolId}`;
      const selectionValue = medicationSelections[noteKey];

      // Only save if user has made a choice (Yes or No)
      // Skip items where selectionValue is null (no button clicked)
      if (selectionValue === null || selectionValue === undefined) {
        return;
      }

      const note = medicationNotes[noteKey];
      const refillInfo = refillInfos?.[noteKey];
      const adminSite = administrationSites?.[noteKey];

      const payload: ManualSelectionPayload = {
        item_type: sel.type as "vaccine" | "medication",
        item_key: sel.protocol.protocolId,
        action: selectionValue ? "select" : "deselect",
      };

      if (note) {
        payload.note = note;
      }

      // Use quantity from the ManualSelection object (set by the modal)
      if (sel.quantity) {
        payload.quantity = sel.quantity;
      }

      if (refillInfo) {
        payload.refill_info = refillInfo;
      }

      if (adminSite) {
        payload.administration_site = adminSite;
      }

      manualItemsToSave.push(payload);
    });

    // Save all selections (AI + manual) in one combined API call
    if (onSaveSelections) {
      try {
        await onSaveSelections(
          manualItemsToSave.length > 0 ? manualItemsToSave : undefined,
        );

        // Mark manual items as saved
        if (manualItemsToSave.length > 0) {
          const newSavedKeys = new Set(savedManualSelectionKeys);
          manualItemsToSave.forEach((item) => {
            const key = `manual-${item.item_type}-${item.item_key}`;
            newSavedKeys.add(key);
          });
          setSavedManualSelectionKeys(newSavedKeys);
        }
      } catch (error) {
        console.error("Failed to save selections:", error);
      }
    }
  };

  // Helper function to render full protocol information in a clean, readable format
  const renderProtocolDetails = (
    protocol: VaccineProtocol | null | undefined,
  ) => {
    if (!protocol) return null;

    const labelStyle = {
      fontSize: "12px",
      color: "#6b7280",
      fontWeight: "600" as const,
    };

    const valueStyle = {
      fontSize: "13px",
      color: "#374151",
      marginBottom: "8px",
    };

    const sectionTitleStyle = {
      fontSize: "13px",
      fontWeight: "600" as const,
      color: "#374151",
      marginTop: "16px",
      marginBottom: "8px",
      paddingBottom: "4px",
      borderBottom: "1px solid #e5e7eb",
    };

    return (
      <div style={{ fontSize: "13px", color: "#374151", lineHeight: "1.6" }}>
        {/* Basic Info */}
        <div style={{ marginBottom: "12px" }}>
          <span style={labelStyle}>Protocol ID:</span> {protocol.protocolId}
          <br />
          <span style={labelStyle}>Type:</span> {protocol.protocolType} |{" "}
          <span style={labelStyle}>Category:</span> {protocol.category} |{" "}
          <span style={labelStyle}>Age Group:</span> {protocol.ageGroup}
        </div>

        {/* Eligibility */}
        {protocol.eligibility && (
          <div>
            <div style={sectionTitleStyle}>Eligibility</div>
            <div style={valueStyle}>
              <span style={labelStyle}>Age:</span>{" "}
              {protocol.eligibility.min_age_years ?? "N/A"} -{" "}
              {protocol.eligibility.max_age_years ?? "No max"} years
              {protocol.eligibility.min_weight_kg && (
                <>
                  <br />
                  <span style={labelStyle}>Min Weight:</span>{" "}
                  {protocol.eligibility.min_weight_kg} kg (
                  {protocol.eligibility.min_weight_lbs} lbs)
                </>
              )}
            </div>
            {protocol.eligibility.pregnancy && (
              <div style={valueStyle}>
                <span style={labelStyle}>Pregnancy:</span>{" "}
                {protocol.eligibility.pregnancy}
                {protocol.eligibility.pregnancy_notes && (
                  <> - {protocol.eligibility.pregnancy_notes}</>
                )}
              </div>
            )}
            {protocol.eligibility.breastfeeding && (
              <div style={valueStyle}>
                <span style={labelStyle}>Breastfeeding:</span>{" "}
                {protocol.eligibility.breastfeeding}
                {protocol.eligibility.breastfeeding_notes && (
                  <> - {protocol.eligibility.breastfeeding_notes}</>
                )}
              </div>
            )}
          </div>
        )}

        {/* Contraindications */}
        {protocol.contraindications &&
          (protocol.contraindications.allergies?.length > 0 ||
            protocol.contraindications.conditions?.length > 0) && (
            <div>
              <div style={sectionTitleStyle}>Contraindications</div>
              {protocol.contraindications.allergies &&
                protocol.contraindications.allergies.length > 0 && (
                  <div style={valueStyle}>
                    <span style={labelStyle}>Allergies:</span>
                    <ul style={{ margin: "4px 0 0 0", paddingLeft: "20px" }}>
                      {protocol.contraindications.allergies.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              {protocol.contraindications.conditions &&
                protocol.contraindications.conditions.length > 0 && (
                  <div style={valueStyle}>
                    <span style={labelStyle}>Conditions:</span>
                    <ul style={{ margin: "4px 0 0 0", paddingLeft: "20px" }}>
                      {protocol.contraindications.conditions.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              {protocol.contraindications.condition_details &&
                Object.keys(protocol.contraindications.condition_details)
                  .length > 0 && (
                  <div style={valueStyle}>
                    {Object.entries(
                      protocol.contraindications.condition_details,
                    ).map(([key, value]) => (
                      <div key={key}>
                        <span style={labelStyle}>
                          {key.replace(/_/g, " ")}:
                        </span>{" "}
                        {value}
                      </div>
                    ))}
                  </div>
                )}
            </div>
          )}

        {/* Drug Interactions */}
        {protocol.drugInteractions &&
          (protocol.drugInteractions.contraindicated?.length > 0 ||
            protocol.drugInteractions.reduced_efficacy?.length > 0 ||
            protocol.drugInteractions.increased_toxicity?.length > 0 ||
            protocol.drugInteractions.monitoring_required?.length > 0) && (
            <div>
              <div style={sectionTitleStyle}>Drug Interactions</div>
              {protocol.drugInteractions.contraindicated &&
                protocol.drugInteractions.contraindicated.length > 0 && (
                  <div style={valueStyle}>
                    <span style={labelStyle}>Contraindicated:</span>{" "}
                    {protocol.drugInteractions.contraindicated.join(", ")}
                  </div>
                )}
              {protocol.drugInteractions.reduced_efficacy &&
                protocol.drugInteractions.reduced_efficacy.length > 0 && (
                  <div style={valueStyle}>
                    <span style={labelStyle}>Reduced Efficacy:</span>{" "}
                    {protocol.drugInteractions.reduced_efficacy.join(", ")}
                  </div>
                )}
              {protocol.drugInteractions.increased_toxicity &&
                protocol.drugInteractions.increased_toxicity.length > 0 && (
                  <div style={valueStyle}>
                    <span style={labelStyle}>Increased Toxicity:</span>{" "}
                    {protocol.drugInteractions.increased_toxicity.join(", ")}
                  </div>
                )}
              {protocol.drugInteractions.monitoring_required &&
                protocol.drugInteractions.monitoring_required.length > 0 && (
                  <div style={valueStyle}>
                    <span style={labelStyle}>Monitoring Required:</span>{" "}
                    {protocol.drugInteractions.monitoring_required.join(", ")}
                  </div>
                )}
            </div>
          )}

        {/* Dosing */}
        {protocol.dosing && (
          <div>
            <div style={sectionTitleStyle}>Dosing</div>
            {protocol.dosing.adult && (
              <div style={valueStyle}>
                <span style={labelStyle}>Adult:</span>{" "}
                {protocol.dosing.adult.dose}, {protocol.dosing.adult.frequency},{" "}
                {protocol.dosing.adult.route}
                {protocol.dosing.adult.instructions && (
                  <>
                    <br />
                    <span style={labelStyle}>Instructions:</span>{" "}
                    {protocol.dosing.adult.instructions}
                  </>
                )}
              </div>
            )}
            {protocol.dosing.pediatric &&
              protocol.dosing.pediatric.length > 0 && (
                <div style={valueStyle}>
                  <span style={labelStyle}>Pediatric:</span>
                  {protocol.dosing.pediatric.map((ped: any, i: number) => (
                    <div
                      key={i}
                      style={{ marginLeft: "12px", marginTop: "4px" }}
                    >
                      <strong>{ped.age_range}:</strong> {ped.dose},{" "}
                      {ped.frequency}
                      {ped.instructions && (
                        <>
                          <br />
                          <span style={{ marginLeft: "12px" }}>
                            {ped.instructions}
                          </span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}

        {/* Timing */}
        {protocol.timing &&
          (protocol.timing.minimum_days_before_travel ||
            protocol.timing.booster_interval) && (
            <div>
              <div style={sectionTitleStyle}>Timing</div>
              <div style={valueStyle}>
                {protocol.timing.minimum_days_before_travel && (
                  <>
                    <span style={labelStyle}>Minimum days before travel:</span>{" "}
                    {protocol.timing.minimum_days_before_travel}
                    <br />
                  </>
                )}
                {protocol.timing.start_days_before && (
                  <>
                    <span style={labelStyle}>Start days before:</span>{" "}
                    {protocol.timing.start_days_before}
                    <br />
                  </>
                )}
                {protocol.timing.days_after_leaving && (
                  <>
                    <span style={labelStyle}>Days after leaving:</span>{" "}
                    {protocol.timing.days_after_leaving}
                    <br />
                  </>
                )}
                {protocol.timing.booster_interval && (
                  <>
                    <span style={labelStyle}>Booster:</span>{" "}
                    {protocol.timing.booster_interval}
                  </>
                )}
              </div>
            </div>
          )}

        {/* Counseling Points */}
        {protocol.counselingPoints && protocol.counselingPoints.length > 0 && (
          <div>
            <div style={sectionTitleStyle}>Counseling Points</div>
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
              {protocol.counselingPoints.map((point, i) => (
                <li key={i} style={{ marginBottom: "4px" }}>
                  {point}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Side Effects */}
        {protocol.sideEffects &&
          (protocol.sideEffects.common?.length > 0 ||
            protocol.sideEffects.serious?.length > 0) && (
            <div>
              <div style={sectionTitleStyle}>Side Effects</div>
              {protocol.sideEffects.common &&
                protocol.sideEffects.common.length > 0 && (
                  <div style={valueStyle}>
                    <span style={labelStyle}>Common:</span>{" "}
                    {protocol.sideEffects.common.join(", ")}
                  </div>
                )}
              {protocol.sideEffects.serious &&
                protocol.sideEffects.serious.length > 0 && (
                  <div style={valueStyle}>
                    <span style={labelStyle}>Serious:</span>
                    <ul style={{ margin: "4px 0 0 0", paddingLeft: "20px" }}>
                      {protocol.sideEffects.serious.map((effect, i) => (
                        <li key={i}>{effect}</li>
                      ))}
                    </ul>
                  </div>
                )}
              {protocol.sideEffects.when_to_seek_care && (
                <div style={valueStyle}>
                  <span style={labelStyle}>When to seek care:</span>{" "}
                  {protocol.sideEffects.when_to_seek_care}
                </div>
              )}
            </div>
          )}

        {/* Storage */}
        {protocol.storage &&
          (protocol.storage.temperature ||
            protocol.storage.special_instructions) && (
            <div>
              <div style={sectionTitleStyle}>Storage</div>
              <div style={valueStyle}>
                {protocol.storage.temperature && (
                  <>
                    <span style={labelStyle}>Temperature:</span>{" "}
                    {protocol.storage.temperature}
                    <br />
                  </>
                )}
                {protocol.storage.special_instructions && (
                  <>
                    <span style={labelStyle}>Special Instructions:</span>{" "}
                    {protocol.storage.special_instructions}
                  </>
                )}
              </div>
            </div>
          )}

        {/* Notes */}
        {protocol.notes && (
          <div>
            <div style={sectionTitleStyle}>Notes</div>
            <div style={valueStyle}>{protocol.notes}</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="info-card medication-checklist-section">
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
            marginBottom: "16px",
            padding: "8px 0",
            borderBottom: "2px solid #e5e7eb",
            paddingBottom: "12px",
          }}
        >
          <div>
            <h3 style={{ margin: 0 }}>Medication Recommendations</h3>
            <p
              style={{
                margin: "6px 0 0 0",
                fontSize: "13px",
                color: "#1e40af",
                fontWeight: "600",
              }}
            >
              <strong>Important:</strong> AI-generated recommendations based on
              CDC guidelines. Clinical judgment should always be applied.
            </p>
          </div>
          <button
            onClick={() => setShowSelectionModal(true)}
            style={{
              padding: "10px 20px",
              backgroundColor: "#14b8a6",
              color: "white",
              border: "none",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "background-color 0.2s",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#0d9488";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#14b8a6";
            }}
          >
            Manually Add
          </button>
        </div>

        {loadingRecommendations && (
          <div
            style={{
              padding: "24px",
              textAlign: "center",
              color: "#6b7280",
            }}
          >
            <div
              style={{
                display: "inline-block",
                width: "24px",
                height: "24px",
                border: "3px solid #e5e7eb",
                borderTop: "3px solid #14b8a6",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                marginBottom: "12px",
              }}
            />
            <p style={{ margin: 0 }}>Loading AI recommendations...</p>
          </div>
        )}

        {recommendationsError && (
          <div
            style={{
              padding: "16px",
              backgroundColor: "#fef2f2",
              borderRadius: "8px",
              border: "1px solid #fecaca",
              color: "#991b1b",
            }}
          >
            {recommendationsError}
          </div>
        )}

        {!loadingRecommendations &&
          !recommendationsError &&
          aiRecommendations &&
          aiRecommendations.recommendation.vaccines.filter(shouldShowVaccine)
            .length === 0 &&
          aiRecommendations.recommendation.medications.filter(
            (m) => m.status === "RECOMMENDED" || m.status === "CONDITIONAL",
          ).length === 0 && (
            <div
              style={{
                padding: "16px",
                backgroundColor: "#f0f9ff",
                borderRadius: "8px",
                border: "1px solid #bae6fd",
                color: "#0c4a6e",
                textAlign: "center",
              }}
            >
              <p style={{ margin: 0, fontWeight: "500" }}>
                No vaccine or medication recommendations available for this
                traveler.
              </p>
            </div>
          )}

        {aiRecommendations && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            {/* Vaccines Section - Collapsible */}
            {(aiRecommendations.recommendation.vaccines.filter(
              shouldShowVaccine,
            ).length > 0 ||
              (aiRecommendations.recommendation.notRecommended?.vaccines &&
                aiRecommendations.recommendation.notRecommended.vaccines
                  .length > 0) ||
              (aiRecommendations.recommendation.pharmacistAddedVaccines || []).length > 0 ||
              manualSelections.filter((s) => s.type === "vaccine").length > 0) && (
              <div
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                  border: "1px solid #e5e7eb",
                }}
              >
                {/* Collapsible Header */}
                <div
                  onClick={() => setVaccinesExpanded(!vaccinesExpanded)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "8px",
                    padding: "16px 20px",
                    backgroundColor: "#f0fdfa",
                    borderBottom: vaccinesExpanded
                      ? "1px solid #e5e7eb"
                      : "none",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#ccfbf1";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#f0fdfa";
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <svg
                      style={{
                        width: "18px",
                        height: "18px",
                        transform: vaccinesExpanded
                          ? "rotate(0deg)"
                          : "rotate(-90deg)",
                        transition: "transform 0.2s",
                        color: "#0d9488",
                      }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                    <h4
                      style={{
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#0d9488",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      Vaccines
                    </h4>
                    <span
                      style={{
                        padding: "4px 10px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: "700",
                        backgroundColor: "#14b8a6",
                        color: "white",
                      }}
                    >
                      {aiRecommendations.recommendation.vaccines.filter(
                        shouldShowVaccine,
                      ).length +
                        (aiRecommendations.recommendation.notRecommended
                          ?.vaccines?.length || 0) +
                        (
                          aiRecommendations.recommendation
                            .pharmacistAddedVaccines || []
                        ).filter((v) => {
                          const selectionKey = `manual-vaccine-${v.protocol_id}`;
                          // Exclude vaccines that exist in NOT_RECOMMENDED list
                          const notRecVaccines =
                            aiRecommendations.recommendation.notRecommended
                              ?.vaccines || [];
                          const existsInNotRec = notRecVaccines.some(
                            (nrv) => nrv.protocol_id === v.protocol_id,
                          );
                          return (
                            medicationSelections[selectionKey] !== false &&
                            !existsInNotRec
                          );
                        }).length +
                        // Include current session manual vaccine selections
                        manualSelections
                          .filter((s) => s.type === "vaccine")
                          .filter(
                            (s) =>
                              !(
                                aiRecommendations.recommendation
                                  .pharmacistAddedVaccines || []
                              ).some(
                                (v) => v.protocol_id === s.protocol.protocolId,
                              ),
                          )
                          .filter((s) => {
                            const selectionKey = `manual-vaccine-${s.protocol.protocolId}`;
                            return medicationSelections[selectionKey] !== false;
                          }).length}
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      fontWeight: "500",
                    }}
                  >
                    {vaccinesExpanded ? "Click to collapse" : "Click to expand"}
                  </span>
                </div>

                {/* Vaccines Content */}
                {vaccinesExpanded && (
                  <div style={{ padding: "16px" }}>
                    <div
                      style={{
                        display: "grid",
                        gap: "12px",
                      }}
                    >
                      {/* Pharmacist Added Vaccines */}
                      {(
                        aiRecommendations.recommendation
                          .pharmacistAddedVaccines || []
                      )
                        .filter((vaccine) => {
                          // Exclude vaccines that exist in NOT_RECOMMENDED list
                          const notRecVaccines =
                            aiRecommendations.recommendation.notRecommended
                              ?.vaccines || [];
                          return !notRecVaccines.some(
                            (nrv) => nrv.protocol_id === vaccine.protocol_id,
                          );
                        })
                        .map((vaccine, idx) => (
                          <div
                            key={`pharmacist-vaccine-${idx}`}
                            style={{
                              backgroundColor: "#ffffff",
                              borderRadius: "10px",
                              border: "1px solid #e5e7eb",
                              borderLeft: "4px solid #14b8a6",
                              transition: "all 0.2s",
                              overflow: "hidden",
                            }}
                          >
                            {/* Collapsible Header */}
                            <div
                              onClick={() =>
                                toggleCard(`pharmacist-vaccine-${idx}`)
                              }
                              style={{
                                padding: "12px 16px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: "8px",
                                cursor: "pointer",
                                backgroundColor: expandedCards[
                                  `pharmacist-vaccine-${idx}`
                                ]
                                  ? "#f9fafb"
                                  : "#ffffff",
                                transition: "background-color 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#f9fafb";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  expandedCards[`pharmacist-vaccine-${idx}`]
                                    ? "#f9fafb"
                                    : "#ffffff";
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                  flex: 1,
                                }}
                              >
                                <svg
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    color: "#14b8a6",
                                    transform: expandedCards[
                                      `pharmacist-vaccine-${idx}`
                                    ]
                                      ? "rotate(90deg)"
                                      : "rotate(0deg)",
                                    transition: "transform 0.2s",
                                    flexShrink: 0,
                                  }}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                                <span
                                  style={{
                                    fontWeight: "700",
                                    color: "#1f2937",
                                    fontSize: "15px",
                                  }}
                                >
                                  {vaccine.name}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <span
                                  style={{
                                    padding: "4px 10px",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    backgroundColor: "#ccfbf1",
                                    color: "#0d9488",
                                    border: "1px solid #5eead4",
                                  }}
                                >
                                  PHARMACIST ADDED
                                </span>
                                {/* Yes/No Selection Buttons */}
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    onClick={() =>
                                      handleManualSelectionChange(
                                        vaccine.protocol_id,
                                        "vaccine",
                                        true,
                                      )
                                    }
                                    style={{
                                      padding: "4px 10px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      backgroundColor:
                                        medicationSelections[
                                          `manual-vaccine-${vaccine.protocol_id}`
                                        ] === true
                                          ? "#dcfce7"
                                          : "white",
                                      color:
                                        medicationSelections[
                                          `manual-vaccine-${vaccine.protocol_id}`
                                        ] === true
                                          ? "#166534"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleManualSelectionChange(
                                        vaccine.protocol_id,
                                        "vaccine",
                                        false,
                                      )
                                    }
                                    style={{
                                      padding: "4px 10px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      backgroundColor:
                                        medicationSelections[
                                          `manual-vaccine-${vaccine.protocol_id}`
                                        ] === false
                                          ? "#fee2e2"
                                          : "white",
                                      color:
                                        medicationSelections[
                                          `manual-vaccine-${vaccine.protocol_id}`
                                        ] === false
                                          ? "#991b1b"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    No
                                  </button>
                                </div>
                                {/* Refills Dropdown */}
                                {onRefillInfoChange && onAdministrationSiteChange && (
                                  <RefillsDropdown
                                    medicationId={`manual-vaccine-${vaccine.protocol_id}`}
                                    refillInfo={refillInfos?.[`manual-vaccine-${vaccine.protocol_id}`] || vaccine.selectionDetails?.refillInfo || ""}
                                    administrationSite={administrationSites?.[`manual-vaccine-${vaccine.protocol_id}`] || vaccine.selectionDetails?.administrationSite || null}
                                    quantity={quantities?.[`manual-vaccine-${vaccine.protocol_id}`] || vaccine.selectionDetails?.quantity || ""}
                                    onRefillInfoChange={onRefillInfoChange}
                                    onAdministrationSiteChange={onAdministrationSiteChange}
                                    onQuantityChange={onQuantityChange}
                                    onSaveDetails={onSaveDetails}
                                    note={medicationNotes[`manual-vaccine-${vaccine.protocol_id}`] || vaccine.selectionDetails?.pharmacistNote || ""}
                                    isSaving={savingNotes[`manual-vaccine-${vaccine.protocol_id}`]}
                                    isVaccine={true}
                                    onRecordAdministration={handleRecordAdministration}
                                    isSelected={medicationSelections[`manual-vaccine-${vaccine.protocol_id}`] === true}
                                  />
                                )}
                              </div>
                            </div>

                            {/* Expandable Content */}
                            {expandedCards[`pharmacist-vaccine-${idx}`] && (
                              <div style={{ padding: "0 16px 16px 16px" }}>
                                {/* Quick Info Grid */}
                                <div
                                  className="grid grid-cols-1 sm:grid-cols-2"
                                  style={{
                                    gap: "8px",
                                    marginBottom: "12px",
                                    padding: "10px",
                                    backgroundColor: "#f9fafb",
                                    borderRadius: "8px",
                                  }}
                                >
                                  <div style={{ textAlign: "center" }}>
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "2px",
                                      }}
                                    >
                                      Dosing
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#1f2937",
                                        fontWeight: "600",
                                        textAlign: "center",
                                      }}
                                    >
                                      {vaccine.protocolMetadata?.dosing?.adult
                                        ?.dose && (
                                        <div>
                                          Adult:{" "}
                                          {
                                            vaccine.protocolMetadata.dosing
                                              .adult.dose
                                          }
                                        </div>
                                      )}
                                      {vaccine.protocolMetadata?.dosing
                                        ?.pediatric &&
                                        Array.isArray(
                                          vaccine.protocolMetadata.dosing
                                            .pediatric,
                                        ) &&
                                        vaccine.protocolMetadata.dosing
                                          .pediatric.length > 0 &&
                                        vaccine.protocolMetadata.dosing.pediatric.map(
                                          (ped: any, i: number) => (
                                            <div key={i}>
                                              {ped.age_range}: {ped.dose}
                                            </div>
                                          ),
                                        )}
                                      {!vaccine.protocolMetadata?.dosing?.adult
                                        ?.dose &&
                                        (!vaccine.protocolMetadata?.dosing
                                          ?.pediatric ||
                                          vaccine.protocolMetadata.dosing
                                            .pediatric.length === 0) &&
                                        (vaccine.doses || "N/A")}
                                    </div>
                                  </div>
                                  <div style={{ textAlign: "center" }}>
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "2px",
                                      }}
                                    >
                                      Route
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "13px",
                                        color: "#1f2937",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {vaccine.protocolMetadata?.dosing?.adult
                                        ?.route ||
                                        vaccine.route ||
                                        "injection"}
                                    </div>
                                  </div>
                                </div>

                                {/* Timing Information */}
                                {vaccine.protocolMetadata?.timing &&
                                  (vaccine.protocolMetadata.timing
                                    .minimum_days_before_travel ||
                                  vaccine.protocolMetadata.timing
                                    .booster_interval ? (
                                    <div
                                      style={{
                                        marginBottom: "12px",
                                        padding: "10px",
                                        backgroundColor: "#fef3c7",
                                        borderRadius: "8px",
                                        border: "1px solid #fde047",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "10px",
                                          color: "#92400e",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "8px",
                                        }}
                                      >
                                        Timing Information
                                      </div>
                                      {vaccine.protocolMetadata.timing
                                        .minimum_days_before_travel && (
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#78350f",
                                            marginBottom: "6px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                          }}
                                        >
                                          <span style={{ fontWeight: "600" }}>
                                            Minimum days before travel:
                                          </span>
                                          <span style={{ fontWeight: "700" }}>
                                            {
                                              vaccine.protocolMetadata.timing
                                                .minimum_days_before_travel
                                            }{" "}
                                            days
                                          </span>
                                        </div>
                                      )}
                                      {vaccine.protocolMetadata.timing
                                        .booster_interval && (
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#78350f",
                                            lineHeight: "1.5",
                                          }}
                                        >
                                          <span style={{ fontWeight: "600" }}>
                                            Booster:
                                          </span>{" "}
                                          {
                                            vaccine.protocolMetadata.timing
                                              .booster_interval
                                          }
                                        </div>
                                      )}
                                    </div>
                                  ) : null)}

                                {/* Brands */}
                                {vaccine.brands &&
                                  vaccine.brands.length > 0 && (
                                    <div
                                      style={{
                                        marginBottom: "12px",
                                        padding: "10px",
                                        backgroundColor: "#f9fafb",
                                        borderRadius: "8px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "10px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Brands
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#1f2937",
                                        }}
                                      >
                                        {vaccine.brands.join(", ")}
                                      </div>
                                    </div>
                                  )}

                                {/* Age Group */}
                                {vaccine.age_group && (
                                  <div style={{ marginBottom: "12px" }}>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Age Group
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#374151",
                                        backgroundColor: "#ffffff",
                                        padding: "8px 10px",
                                        borderRadius: "4px",
                                        border: "1px solid #e5e7eb",
                                      }}
                                    >
                                      {vaccine.age_group}
                                    </div>
                                  </div>
                                )}

                                {/* Therapy Class */}
                                {vaccine.therapy_class && (
                                  <div style={{ marginBottom: "12px" }}>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Therapy Class
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#374151",
                                        backgroundColor: "#ffffff",
                                        padding: "8px 10px",
                                        borderRadius: "4px",
                                        border: "1px solid #e5e7eb",
                                      }}
                                    >
                                      {vaccine.therapy_class}
                                    </div>
                                  </div>
                                )}

                                {/* Notes from Protocol */}
                                {vaccine.notes && (
                                  <div style={{ marginBottom: "12px" }}>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Protocol Notes
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#374151",
                                        backgroundColor: "#eff6ff",
                                        padding: "10px",
                                        borderRadius: "4px",
                                        border: "1px solid #dbeafe",
                                        lineHeight: "1.5",
                                      }}
                                    >
                                      {vaccine.notes}
                                    </div>
                                  </div>
                                )}

                                {/* Counseling Points */}
                                {vaccine.protocolMetadata?.counselingPoints &&
                                  vaccine.protocolMetadata.counselingPoints
                                    .length > 0 && (
                                    <div style={{ marginBottom: "12px" }}>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Counseling Points
                                      </div>
                                      <ul
                                        style={{
                                          fontSize: "12px",
                                          color: "#374151",
                                          backgroundColor: "#fffbeb",
                                          padding: "10px 10px 10px 28px",
                                          borderRadius: "4px",
                                          border: "1px solid #fde68a",
                                          lineHeight: "1.6",
                                          margin: 0,
                                        }}
                                      >
                                        {vaccine.protocolMetadata.counselingPoints.map(
                                          (point: string, pointIdx: number) => (
                                            <li
                                              key={pointIdx}
                                              style={{
                                                marginBottom:
                                                  pointIdx <
                                                  vaccine.protocolMetadata!
                                                    .counselingPoints!.length -
                                                    1
                                                    ? "4px"
                                                    : 0,
                                              }}
                                            >
                                              {point}
                                            </li>
                                          ),
                                        )}
                                      </ul>
                                    </div>
                                  )}

                                {/* Contraindications - Allergies */}
                                {vaccine.protocolMetadata?.contraindications
                                  ?.allergies &&
                                  vaccine.protocolMetadata.contraindications
                                    .allergies.length > 0 && (
                                    <div style={{ marginBottom: "12px" }}>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Allergy Contraindications
                                      </div>
                                      <ul
                                        style={{
                                          fontSize: "12px",
                                          color: "#374151",
                                          backgroundColor: "#fef2f2",
                                          padding: "10px 10px 10px 28px",
                                          borderRadius: "4px",
                                          border: "1px solid #fecaca",
                                          lineHeight: "1.6",
                                          margin: 0,
                                        }}
                                      >
                                        {vaccine.protocolMetadata.contraindications.allergies.map(
                                          (
                                            allergy: string,
                                            allergyIdx: number,
                                          ) => (
                                            <li
                                              key={allergyIdx}
                                              style={{
                                                marginBottom:
                                                  allergyIdx <
                                                  vaccine.protocolMetadata!
                                                    .contraindications!
                                                    .allergies!.length -
                                                    1
                                                    ? "4px"
                                                    : 0,
                                              }}
                                            >
                                              {allergy}
                                            </li>
                                          ),
                                        )}
                                      </ul>
                                    </div>
                                  )}

                                {/* Contraindications - Conditions */}
                                {vaccine.protocolMetadata?.contraindications
                                  ?.conditions &&
                                  vaccine.protocolMetadata.contraindications
                                    .conditions.length > 0 && (
                                    <div style={{ marginBottom: "12px" }}>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Condition Contraindications
                                      </div>
                                      <ul
                                        style={{
                                          fontSize: "12px",
                                          color: "#374151",
                                          backgroundColor: "#fef2f2",
                                          padding: "10px 10px 10px 28px",
                                          borderRadius: "4px",
                                          border: "1px solid #fecaca",
                                          lineHeight: "1.6",
                                          margin: 0,
                                        }}
                                      >
                                        {vaccine.protocolMetadata.contraindications.conditions.map(
                                          (
                                            condition: string,
                                            conditionIdx: number,
                                          ) => (
                                            <li
                                              key={conditionIdx}
                                              style={{
                                                marginBottom:
                                                  conditionIdx <
                                                  vaccine.protocolMetadata!
                                                    .contraindications!
                                                    .conditions!.length -
                                                    1
                                                    ? "4px"
                                                    : 0,
                                              }}
                                            >
                                              {condition}
                                            </li>
                                          ),
                                        )}
                                      </ul>
                                    </div>
                                  )}

                                {/* More Details Button - Teal Styled */}
                                <button
                                  onClick={() =>
                                    toggleDetails(`pharmacist-vaccine-${idx}`)
                                  }
                                  style={{
                                    width: "100%",
                                    padding: "10px 14px",
                                    backgroundColor: "#ccfbf1",
                                    border: "1px solid #5eead4",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: "#0d9488",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "8px",
                                    transition: "all 0.2s",
                                    marginTop: "12px",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "#99f6e4";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "#ccfbf1";
                                  }}
                                >
                                  <span>More Details</span>
                                  <svg
                                    style={{
                                      width: "16px",
                                      height: "16px",
                                      transform: expandedDetails[
                                        `pharmacist-vaccine-${idx}`
                                      ]
                                        ? "rotate(180deg)"
                                        : "rotate(0deg)",
                                      transition: "transform 0.2s",
                                    }}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 9l-7 7-7-7"
                                    />
                                  </svg>
                                </button>

                                {/* Expandable More Details */}
                                {expandedDetails[
                                  `pharmacist-vaccine-${idx}`
                                ] && (
                                  <div
                                    style={{
                                      marginTop: "8px",
                                      padding: "14px",
                                      backgroundColor: "#f0fdfa",
                                      borderRadius: "8px",
                                      border: "1px solid #99f6e4",
                                    }}
                                  >
                                    {/* Protocol ID */}
                                    <div style={{ marginBottom: "12px" }}>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Protocol ID
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#374151",
                                          backgroundColor: "#ffffff",
                                          padding: "8px 10px",
                                          borderRadius: "4px",
                                          border: "1px solid #e5e7eb",
                                        }}
                                      >
                                        {vaccine.protocol_id}
                                      </div>
                                    </div>

                                    {/* Timing Information */}
                                    {vaccine.protocolMetadata?.timing && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Timing Details
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#ffffff",
                                            padding: "8px 10px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                            lineHeight: "1.6",
                                          }}
                                        >
                                          {vaccine.protocolMetadata.timing
                                            .minimum_days_before_travel && (
                                            <div>
                                              <strong>
                                                Minimum days before travel:
                                              </strong>{" "}
                                              {
                                                vaccine.protocolMetadata.timing
                                                  .minimum_days_before_travel
                                              }
                                            </div>
                                          )}
                                          {vaccine.protocolMetadata.timing
                                            .booster_interval && (
                                            <div>
                                              <strong>Booster interval:</strong>{" "}
                                              {
                                                vaccine.protocolMetadata.timing
                                                  .booster_interval
                                              }
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Full Dosing Information */}
                                    {vaccine.protocolMetadata?.dosing && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Full Dosing Information
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#ffffff",
                                            padding: "10px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                            lineHeight: "1.6",
                                          }}
                                        >
                                          {vaccine.protocolMetadata.dosing
                                            .adult && (
                                            <div
                                              style={{ marginBottom: "8px" }}
                                            >
                                              <strong>Adult:</strong>
                                              <div
                                                style={{
                                                  marginLeft: "12px",
                                                  marginTop: "4px",
                                                }}
                                              >
                                                {vaccine.protocolMetadata.dosing
                                                  .adult.dose && (
                                                  <div>
                                                    Dose:{" "}
                                                    {
                                                      vaccine.protocolMetadata
                                                        .dosing.adult.dose
                                                    }
                                                  </div>
                                                )}
                                                {vaccine.protocolMetadata.dosing
                                                  .adult.route && (
                                                  <div>
                                                    Route:{" "}
                                                    {
                                                      vaccine.protocolMetadata
                                                        .dosing.adult.route
                                                    }
                                                  </div>
                                                )}
                                                {vaccine.protocolMetadata.dosing
                                                  .adult.frequency && (
                                                  <div>
                                                    Frequency:{" "}
                                                    {
                                                      vaccine.protocolMetadata
                                                        .dosing.adult.frequency
                                                    }
                                                  </div>
                                                )}
                                                {vaccine.protocolMetadata.dosing
                                                  .adult.instructions && (
                                                  <div>
                                                    Instructions:{" "}
                                                    {
                                                      vaccine.protocolMetadata
                                                        .dosing.adult
                                                        .instructions
                                                    }
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                          {vaccine.protocolMetadata.dosing
                                            .pediatric &&
                                            vaccine.protocolMetadata.dosing
                                              .pediatric.length > 0 && (
                                              <div>
                                                <strong>Pediatric:</strong>
                                                {vaccine.protocolMetadata.dosing.pediatric.map(
                                                  (
                                                    ped: any,
                                                    pedIdx: number,
                                                  ) => (
                                                    <div
                                                      key={pedIdx}
                                                      style={{
                                                        marginLeft: "12px",
                                                        marginTop: "4px",
                                                        paddingLeft: "8px",
                                                        borderLeft:
                                                          "2px solid #e5e7eb",
                                                      }}
                                                    >
                                                      {ped.age_range && (
                                                        <div>
                                                          <strong>
                                                            {ped.age_range}:
                                                          </strong>
                                                        </div>
                                                      )}
                                                      {ped.dose && (
                                                        <div>
                                                          Dose: {ped.dose}
                                                        </div>
                                                      )}
                                                      {ped.frequency && (
                                                        <div>
                                                          Frequency:{" "}
                                                          {ped.frequency}
                                                        </div>
                                                      )}
                                                      {ped.instructions && (
                                                        <div>
                                                          Instructions:{" "}
                                                          {ped.instructions}
                                                        </div>
                                                      )}
                                                    </div>
                                                  ),
                                                )}
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Category */}
                                    {vaccine.protocolMetadata?.category && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Category
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#ffffff",
                                            padding: "8px 10px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                          }}
                                        >
                                          {vaccine.protocolMetadata.category}
                                        </div>
                                      </div>
                                    )}

                                    {/* Protocol Type */}
                                    {vaccine.protocolMetadata?.protocolType && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Protocol Type
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#ffffff",
                                            padding: "8px 10px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                          }}
                                        >
                                          {
                                            vaccine.protocolMetadata
                                              .protocolType
                                          }
                                        </div>
                                      </div>
                                    )}

                                    {/* Eligibility - Pregnancy/Breastfeeding */}
                                    {vaccine.protocolMetadata?.eligibility &&
                                      (vaccine.protocolMetadata.eligibility
                                        .pregnancy ||
                                        vaccine.protocolMetadata.eligibility
                                          .breastfeeding ||
                                        vaccine.protocolMetadata.eligibility
                                          .min_age_years ||
                                        vaccine.protocolMetadata.eligibility
                                          .max_age_years) && (
                                        <div style={{ marginBottom: "12px" }}>
                                          <div
                                            style={{
                                              fontSize: "11px",
                                              color: "#6b7280",
                                              fontWeight: "600",
                                              textTransform: "uppercase",
                                              marginBottom: "4px",
                                            }}
                                          >
                                            Eligibility & Warnings
                                          </div>
                                          <div
                                            style={{
                                              fontSize: "12px",
                                              color: "#374151",
                                              backgroundColor: "#fef2f2",
                                              padding: "10px",
                                              borderRadius: "4px",
                                              border: "1px solid #fecaca",
                                              lineHeight: "1.6",
                                            }}
                                          >
                                            {(vaccine.protocolMetadata
                                              .eligibility.min_age_years !==
                                              null ||
                                              vaccine.protocolMetadata
                                                .eligibility.max_age_years !==
                                                null) && (
                                              <div
                                                style={{ marginBottom: "6px" }}
                                              >
                                                <strong>Age Range:</strong>{" "}
                                                {vaccine.protocolMetadata
                                                  .eligibility.min_age_years ??
                                                  0}{" "}
                                                -{" "}
                                                {vaccine.protocolMetadata
                                                  .eligibility.max_age_years ??
                                                  "No max"}{" "}
                                                years
                                              </div>
                                            )}
                                            {vaccine.protocolMetadata
                                              .eligibility.pregnancy && (
                                              <div
                                                style={{ marginBottom: "6px" }}
                                              >
                                                <strong>Pregnancy:</strong>{" "}
                                                <span
                                                  style={{
                                                    color:
                                                      vaccine.protocolMetadata
                                                        .eligibility
                                                        .pregnancy ===
                                                      "contraindicated"
                                                        ? "#dc2626"
                                                        : "#374151",
                                                    fontWeight:
                                                      vaccine.protocolMetadata
                                                        .eligibility
                                                        .pregnancy ===
                                                      "contraindicated"
                                                        ? "600"
                                                        : "400",
                                                  }}
                                                >
                                                  {
                                                    vaccine.protocolMetadata
                                                      .eligibility.pregnancy
                                                  }
                                                </span>
                                                {vaccine.protocolMetadata
                                                  .eligibility
                                                  .pregnancy_notes && (
                                                  <div
                                                    style={{
                                                      marginLeft: "12px",
                                                      fontSize: "11px",
                                                      color: "#6b7280",
                                                      marginTop: "2px",
                                                    }}
                                                  >
                                                    {
                                                      vaccine.protocolMetadata
                                                        .eligibility
                                                        .pregnancy_notes
                                                    }
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                            {vaccine.protocolMetadata
                                              .eligibility.breastfeeding && (
                                              <div>
                                                <strong>Breastfeeding:</strong>{" "}
                                                <span
                                                  style={{
                                                    color:
                                                      vaccine.protocolMetadata
                                                        .eligibility
                                                        .breastfeeding ===
                                                      "contraindicated"
                                                        ? "#dc2626"
                                                        : "#374151",
                                                    fontWeight:
                                                      vaccine.protocolMetadata
                                                        .eligibility
                                                        .breastfeeding ===
                                                      "contraindicated"
                                                        ? "600"
                                                        : "400",
                                                  }}
                                                >
                                                  {
                                                    vaccine.protocolMetadata
                                                      .eligibility.breastfeeding
                                                  }
                                                </span>
                                                {vaccine.protocolMetadata
                                                  .eligibility
                                                  .breastfeeding_notes && (
                                                  <div
                                                    style={{
                                                      marginLeft: "12px",
                                                      fontSize: "11px",
                                                      color: "#6b7280",
                                                      marginTop: "2px",
                                                    }}
                                                  >
                                                    {
                                                      vaccine.protocolMetadata
                                                        .eligibility
                                                        .breastfeeding_notes
                                                    }
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                    {/* Side Effects */}
                                    {vaccine.protocolMetadata?.sideEffects &&
                                      (vaccine.protocolMetadata.sideEffects
                                        .common?.length > 0 ||
                                        vaccine.protocolMetadata.sideEffects
                                          .serious?.length > 0) && (
                                        <div style={{ marginBottom: "12px" }}>
                                          <div
                                            style={{
                                              fontSize: "11px",
                                              color: "#6b7280",
                                              fontWeight: "600",
                                              textTransform: "uppercase",
                                              marginBottom: "4px",
                                            }}
                                          >
                                            Side Effects
                                          </div>
                                          <div
                                            style={{
                                              fontSize: "12px",
                                              color: "#374151",
                                              backgroundColor: "#fff7ed",
                                              padding: "10px",
                                              borderRadius: "4px",
                                              border: "1px solid #fed7aa",
                                              lineHeight: "1.6",
                                            }}
                                          >
                                            {vaccine.protocolMetadata
                                              .sideEffects.common?.length >
                                              0 && (
                                              <div
                                                style={{ marginBottom: "8px" }}
                                              >
                                                <strong>Common:</strong>
                                                <ul
                                                  style={{
                                                    margin: "4px 0 0 0",
                                                    paddingLeft: "20px",
                                                  }}
                                                >
                                                  {vaccine.protocolMetadata.sideEffects.common.map(
                                                    (
                                                      effect: string,
                                                      i: number,
                                                    ) => (
                                                      <li
                                                        key={i}
                                                        style={{
                                                          marginBottom: "2px",
                                                        }}
                                                      >
                                                        {effect}
                                                      </li>
                                                    ),
                                                  )}
                                                </ul>
                                              </div>
                                            )}
                                            {vaccine.protocolMetadata
                                              .sideEffects.serious?.length >
                                              0 && (
                                              <div
                                                style={{ marginBottom: "8px" }}
                                              >
                                                <strong
                                                  style={{ color: "#dc2626" }}
                                                >
                                                  Serious:
                                                </strong>
                                                <ul
                                                  style={{
                                                    margin: "4px 0 0 0",
                                                    paddingLeft: "20px",
                                                  }}
                                                >
                                                  {vaccine.protocolMetadata.sideEffects.serious.map(
                                                    (
                                                      effect: string,
                                                      i: number,
                                                    ) => (
                                                      <li
                                                        key={i}
                                                        style={{
                                                          marginBottom: "2px",
                                                          color: "#991b1b",
                                                        }}
                                                      >
                                                        {effect}
                                                      </li>
                                                    ),
                                                  )}
                                                </ul>
                                              </div>
                                            )}
                                            {vaccine.protocolMetadata
                                              .sideEffects
                                              .when_to_seek_care && (
                                              <div>
                                                <strong>
                                                  When to Seek Care:
                                                </strong>{" "}
                                                {
                                                  vaccine.protocolMetadata
                                                    .sideEffects
                                                    .when_to_seek_care
                                                }
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                    {/* Drug Interactions */}
                                    {vaccine.protocolMetadata
                                      ?.drugInteractions &&
                                      (vaccine.protocolMetadata.drugInteractions
                                        .contraindicated?.length > 0 ||
                                        vaccine.protocolMetadata
                                          .drugInteractions.monitoring_required
                                          ?.length > 0 ||
                                        vaccine.protocolMetadata
                                          .drugInteractions.reduced_efficacy
                                          ?.length > 0) && (
                                        <div style={{ marginBottom: "12px" }}>
                                          <div
                                            style={{
                                              fontSize: "11px",
                                              color: "#6b7280",
                                              fontWeight: "600",
                                              textTransform: "uppercase",
                                              marginBottom: "4px",
                                            }}
                                          >
                                            Drug Interactions
                                          </div>
                                          <div
                                            style={{
                                              fontSize: "12px",
                                              color: "#374151",
                                              backgroundColor: "#fdf4ff",
                                              padding: "10px",
                                              borderRadius: "4px",
                                              border: "1px solid #f5d0fe",
                                              lineHeight: "1.6",
                                            }}
                                          >
                                            {vaccine.protocolMetadata
                                              .drugInteractions.contraindicated
                                              ?.length > 0 && (
                                              <div
                                                style={{ marginBottom: "8px" }}
                                              >
                                                <strong
                                                  style={{ color: "#dc2626" }}
                                                >
                                                  Contraindicated:
                                                </strong>
                                                <ul
                                                  style={{
                                                    margin: "4px 0 0 0",
                                                    paddingLeft: "20px",
                                                  }}
                                                >
                                                  {vaccine.protocolMetadata.drugInteractions.contraindicated.map(
                                                    (
                                                      drug: string,
                                                      i: number,
                                                    ) => (
                                                      <li
                                                        key={i}
                                                        style={{
                                                          marginBottom: "2px",
                                                        }}
                                                      >
                                                        {drug}
                                                      </li>
                                                    ),
                                                  )}
                                                </ul>
                                              </div>
                                            )}
                                            {vaccine.protocolMetadata
                                              .drugInteractions.reduced_efficacy
                                              ?.length > 0 && (
                                              <div
                                                style={{ marginBottom: "8px" }}
                                              >
                                                <strong>
                                                  Reduced Efficacy:
                                                </strong>
                                                <ul
                                                  style={{
                                                    margin: "4px 0 0 0",
                                                    paddingLeft: "20px",
                                                  }}
                                                >
                                                  {vaccine.protocolMetadata.drugInteractions.reduced_efficacy.map(
                                                    (
                                                      drug: string,
                                                      i: number,
                                                    ) => (
                                                      <li
                                                        key={i}
                                                        style={{
                                                          marginBottom: "2px",
                                                        }}
                                                      >
                                                        {drug}
                                                      </li>
                                                    ),
                                                  )}
                                                </ul>
                                              </div>
                                            )}
                                            {vaccine.protocolMetadata
                                              .drugInteractions
                                              .monitoring_required?.length >
                                              0 && (
                                              <div>
                                                <strong>
                                                  Monitoring Required:
                                                </strong>
                                                <ul
                                                  style={{
                                                    margin: "4px 0 0 0",
                                                    paddingLeft: "20px",
                                                  }}
                                                >
                                                  {vaccine.protocolMetadata.drugInteractions.monitoring_required.map(
                                                    (
                                                      drug: string,
                                                      i: number,
                                                    ) => (
                                                      <li
                                                        key={i}
                                                        style={{
                                                          marginBottom: "2px",
                                                        }}
                                                      >
                                                        {drug}
                                                      </li>
                                                    ),
                                                  )}
                                                </ul>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                    {/* Storage Information */}
                                    {vaccine.protocolMetadata?.storage &&
                                      (vaccine.protocolMetadata.storage
                                        .temperature ||
                                        vaccine.protocolMetadata.storage
                                          .special_instructions) && (
                                        <div style={{ marginBottom: "12px" }}>
                                          <div
                                            style={{
                                              fontSize: "11px",
                                              color: "#6b7280",
                                              fontWeight: "600",
                                              textTransform: "uppercase",
                                              marginBottom: "4px",
                                            }}
                                          >
                                            Storage
                                          </div>
                                          <div
                                            style={{
                                              fontSize: "12px",
                                              color: "#374151",
                                              backgroundColor: "#ecfeff",
                                              padding: "10px",
                                              borderRadius: "4px",
                                              border: "1px solid #a5f3fc",
                                              lineHeight: "1.6",
                                            }}
                                          >
                                            {vaccine.protocolMetadata.storage
                                              .temperature && (
                                              <div
                                                style={{ marginBottom: "4px" }}
                                              >
                                                <strong>Temperature:</strong>{" "}
                                                {
                                                  vaccine.protocolMetadata
                                                    .storage.temperature
                                                }
                                              </div>
                                            )}
                                            {vaccine.protocolMetadata.storage
                                              .special_instructions && (
                                              <div>
                                                <strong>Instructions:</strong>{" "}
                                                {
                                                  vaccine.protocolMetadata
                                                    .storage
                                                    .special_instructions
                                                }
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                )}

                                {/* Pharmacist Note - Inside expandable */}
                                <div style={{ marginTop: "12px" }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      Pharmacist Note
                                    </div>
                                    {onSaveNote && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSaveNote(
                                            `manual-vaccine-${vaccine.protocol_id}`,
                                          );
                                        }}
                                        disabled={
                                          savingNotes[
                                            `manual-vaccine-${vaccine.protocol_id}`
                                          ]
                                        }
                                        style={{
                                          padding: "2px 8px",
                                          fontSize: "10px",
                                          fontWeight: "600",
                                          color: savingNotes[
                                            `manual-vaccine-${vaccine.protocol_id}`
                                          ]
                                            ? "#9ca3af"
                                            : "#3b82f6",
                                          backgroundColor: "transparent",
                                          border: "1px solid",
                                          borderColor: savingNotes[
                                            `manual-vaccine-${vaccine.protocol_id}`
                                          ]
                                            ? "#d1d5db"
                                            : "#3b82f6",
                                          borderRadius: "4px",
                                          cursor: savingNotes[
                                            `manual-vaccine-${vaccine.protocol_id}`
                                          ]
                                            ? "not-allowed"
                                            : "pointer",
                                          transition: "all 0.2s",
                                        }}
                                      >
                                        {savingNotes[
                                          `manual-vaccine-${vaccine.protocol_id}`
                                        ]
                                          ? "Saving Note..."
                                          : "Save Note"}
                                      </button>
                                    )}
                                  </div>
                                  <textarea
                                    placeholder="Add notes for this vaccine..."
                                    value={
                                      medicationNotes[
                                        `manual-vaccine-${vaccine.protocol_id}`
                                      ] ||
                                      vaccine.selectionDetails
                                        ?.pharmacistNote ||
                                      ""
                                    }
                                    onChange={(e) =>
                                      onNoteChange(
                                        `manual-vaccine-${vaccine.protocol_id}`,
                                        e.target.value,
                                      )
                                    }
                                    style={{
                                      width: "100%",
                                      minHeight: "60px",
                                      padding: "10px",
                                      fontSize: "12px",
                                      color: "#374151",
                                      backgroundColor: "#ffffff",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      resize: "vertical",
                                      fontFamily: "inherit",
                                      lineHeight: "1.5",
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}

                      {/* Current Session Manual Vaccine Selections */}
                      {manualSelections
                        .filter((s) => s.type === "vaccine")
                        .filter(
                          (s) =>
                            !(
                              aiRecommendations.recommendation
                                .pharmacistAddedVaccines || []
                            ).some(
                              (v) => v.protocol_id === s.protocol.protocolId,
                            ),
                        )
                        .filter((s) => {
                          const selectionKey = `manual-vaccine-${s.protocol.protocolId}`;
                          return medicationSelections[selectionKey] !== false;
                        })
                        .map((selection, idx) => (
                          <div
                            key={`manual-vaccine-${idx}`}
                            style={{
                              backgroundColor: "#ffffff",
                              borderRadius: "10px",
                              border: "1px solid #e5e7eb",
                              borderLeft: "4px solid #eab308",
                              transition: "all 0.2s",
                              overflow: "hidden",
                            }}
                          >
                            {/* Collapsible Header */}
                            <div
                              onClick={() =>
                                toggleCard(`session-manual-vaccine-${idx}`)
                              }
                              style={{
                                padding: "12px 16px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: "8px",
                                cursor: "pointer",
                                backgroundColor: expandedCards[
                                  `session-manual-vaccine-${idx}`
                                ]
                                  ? "#f9fafb"
                                  : "#ffffff",
                                transition: "background-color 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#f9fafb";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  expandedCards[`session-manual-vaccine-${idx}`]
                                    ? "#f9fafb"
                                    : "#ffffff";
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                  flex: 1,
                                }}
                              >
                                <svg
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    color: "#eab308",
                                    transform: expandedCards[
                                      `session-manual-vaccine-${idx}`
                                    ]
                                      ? "rotate(90deg)"
                                      : "rotate(0deg)",
                                    transition: "transform 0.2s",
                                    flexShrink: 0,
                                  }}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                                <span
                                  style={{
                                    fontWeight: "700",
                                    color: "#1f2937",
                                    fontSize: "15px",
                                  }}
                                >
                                  {selection.protocol.displayName}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <span
                                  style={{
                                    padding: "4px 10px",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    backgroundColor: "#fef3c7",
                                    color: "#92400e",
                                    border: "1px solid #fde047",
                                  }}
                                >
                                  MANUALLY ADDED
                                </span>
                                {/* Yes/No Selection Buttons */}
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    onClick={() =>
                                      handleManualSelectionChange(
                                        selection.protocol.protocolId,
                                        "vaccine",
                                        true,
                                      )
                                    }
                                    style={{
                                      padding: "4px 10px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      backgroundColor:
                                        medicationSelections[
                                          `manual-vaccine-${selection.protocol.protocolId}`
                                        ] === true
                                          ? "#dcfce7"
                                          : "white",
                                      color:
                                        medicationSelections[
                                          `manual-vaccine-${selection.protocol.protocolId}`
                                        ] === true
                                          ? "#166534"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleManualSelectionChange(
                                        selection.protocol.protocolId,
                                        "vaccine",
                                        false,
                                      )
                                    }
                                    style={{
                                      padding: "4px 10px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      backgroundColor:
                                        medicationSelections[
                                          `manual-vaccine-${selection.protocol.protocolId}`
                                        ] === false
                                          ? "#fee2e2"
                                          : "white",
                                      color:
                                        medicationSelections[
                                          `manual-vaccine-${selection.protocol.protocolId}`
                                        ] === false
                                          ? "#991b1b"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    No
                                  </button>
                                </div>
                                {/* Refills Dropdown */}
                                {onRefillInfoChange && onAdministrationSiteChange && (
                                  <RefillsDropdown
                                    medicationId={`manual-vaccine-${selection.protocol.protocolId}`}
                                    refillInfo={refillInfos?.[`manual-vaccine-${selection.protocol.protocolId}`] || ""}
                                    administrationSite={administrationSites?.[`manual-vaccine-${selection.protocol.protocolId}`] || null}
                                    quantity={quantities?.[`manual-vaccine-${selection.protocol.protocolId}`] || ""}
                                    onRefillInfoChange={onRefillInfoChange}
                                    onAdministrationSiteChange={onAdministrationSiteChange}
                                    onQuantityChange={onQuantityChange}
                                    onSaveDetails={onSaveDetails}
                                    note={medicationNotes[`manual-vaccine-${selection.protocol.protocolId}`] || selection.note || ""}
                                    isSaving={savingNotes[`manual-vaccine-${selection.protocol.protocolId}`]}
                                    isVaccine={true}
                                    onRecordAdministration={handleRecordAdministration}
                                    isSelected={medicationSelections[`manual-vaccine-${selection.protocol.protocolId}`] === true}
                                  />
                                )}
                              </div>
                            </div>

                            {/* Expandable Content */}
                            {expandedCards[`session-manual-vaccine-${idx}`] && (
                              <div style={{ padding: "0 16px 16px 16px" }}>
                                {/* Dosing & Route Info */}
                                <div
                                  className="grid grid-cols-1 sm:grid-cols-2"
                                  style={{
                                    marginBottom: "12px",
                                    padding: "10px",
                                    backgroundColor: "#f9fafb",
                                    borderRadius: "8px",
                                    gap: "8px",
                                  }}
                                >
                                  <div style={{ textAlign: "center" }}>
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Dosing
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#1f2937",
                                        fontWeight: "600",
                                        textAlign: "center",
                                      }}
                                    >
                                      {(selection.protocol as any).dosing?.adult
                                        ?.dose && (
                                        <div>
                                          Adult:{" "}
                                          {
                                            (selection.protocol as any).dosing
                                              .adult.dose
                                          }
                                        </div>
                                      )}
                                      {(selection.protocol as any).dosing
                                        ?.pediatric &&
                                        Array.isArray(
                                          (selection.protocol as any).dosing
                                            .pediatric,
                                        ) &&
                                        (selection.protocol as any).dosing
                                          .pediatric.length > 0 &&
                                        (
                                          selection.protocol as any
                                        ).dosing.pediatric.map(
                                          (ped: any, i: number) => (
                                            <div key={i}>
                                              {ped.age_range}: {ped.dose}
                                            </div>
                                          ),
                                        )}
                                      {!(selection.protocol as any).dosing
                                        ?.adult?.dose &&
                                        (!(selection.protocol as any).dosing
                                          ?.pediatric ||
                                          (selection.protocol as any).dosing
                                            .pediatric.length === 0) &&
                                        ((selection.protocol as any).doses ||
                                          "N/A")}
                                    </div>
                                  </div>
                                  <div style={{ textAlign: "center" }}>
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Route
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "13px",
                                        color: "#1f2937",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {(selection.protocol as any).dosing?.adult
                                        ?.route ||
                                        (selection.protocol as any).route ||
                                        "injection"}
                                    </div>
                                  </div>
                                </div>
                                {/* Timing Information */}
                                {(selection.protocol as any).timing &&
                                  ((selection.protocol as any).timing
                                    .minimum_days_before_travel ||
                                  (selection.protocol as any).timing
                                    .booster_interval ? (
                                    <div
                                      style={{
                                        marginBottom: "12px",
                                        padding: "10px",
                                        backgroundColor: "#fef3c7",
                                        borderRadius: "8px",
                                        border: "1px solid #fde047",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "10px",
                                          color: "#92400e",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "8px",
                                        }}
                                      >
                                        Timing Information
                                      </div>
                                      {(selection.protocol as any).timing
                                        .minimum_days_before_travel && (
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#78350f",
                                            marginBottom: "6px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                          }}
                                        >
                                          <span style={{ fontWeight: "600" }}>
                                            Minimum days before travel:
                                          </span>
                                          <span style={{ fontWeight: "700" }}>
                                            {
                                              (selection.protocol as any).timing
                                                .minimum_days_before_travel
                                            }{" "}
                                            days
                                          </span>
                                        </div>
                                      )}
                                      {(selection.protocol as any).timing
                                        .booster_interval && (
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#78350f",
                                            lineHeight: "1.5",
                                          }}
                                        >
                                          <span style={{ fontWeight: "600" }}>
                                            Booster:
                                          </span>{" "}
                                          {
                                            (selection.protocol as any).timing
                                              .booster_interval
                                          }
                                        </div>
                                      )}
                                    </div>
                                  ) : null)}
                                {/* Brands */}
                                {(selection.protocol as any).brands &&
                                  (selection.protocol as any).brands.length >
                                    0 && (
                                    <div
                                      style={{
                                        marginBottom: "12px",
                                        padding: "10px",
                                        backgroundColor: "#f9fafb",
                                        borderRadius: "8px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "10px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Brands
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#1f2937",
                                        }}
                                      >
                                        {(
                                          selection.protocol as any
                                        ).brands.join(", ")}
                                      </div>
                                    </div>
                                  )}

                                {/* Pharmacist Note - Inside expandable */}
                                <div style={{ marginTop: "12px" }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      Pharmacist Note
                                    </div>
                                    {onSaveNote && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSaveNote(
                                            `manual-vaccine-${selection.protocol.protocolId}`,
                                          );
                                        }}
                                        disabled={
                                          savingNotes[
                                            `manual-vaccine-${selection.protocol.protocolId}`
                                          ]
                                        }
                                        style={{
                                          padding: "2px 8px",
                                          fontSize: "10px",
                                          fontWeight: "600",
                                          color: savingNotes[
                                            `manual-vaccine-${selection.protocol.protocolId}`
                                          ]
                                            ? "#9ca3af"
                                            : "#3b82f6",
                                          backgroundColor: "transparent",
                                          border: "1px solid",
                                          borderColor: savingNotes[
                                            `manual-vaccine-${selection.protocol.protocolId}`
                                          ]
                                            ? "#d1d5db"
                                            : "#3b82f6",
                                          borderRadius: "4px",
                                          cursor: savingNotes[
                                            `manual-vaccine-${selection.protocol.protocolId}`
                                          ]
                                            ? "not-allowed"
                                            : "pointer",
                                          transition: "all 0.2s",
                                        }}
                                      >
                                        {savingNotes[
                                          `manual-vaccine-${selection.protocol.protocolId}`
                                        ]
                                          ? "Saving Note..."
                                          : "Save Note"}
                                      </button>
                                    )}
                                  </div>
                                  <textarea
                                    placeholder="Add notes for this vaccine..."
                                    value={
                                      medicationNotes[
                                        `manual-vaccine-${selection.protocol.protocolId}`
                                      ] ||
                                      selection.note ||
                                      ""
                                    }
                                    onChange={(e) =>
                                      onNoteChange(
                                        `manual-vaccine-${selection.protocol.protocolId}`,
                                        e.target.value,
                                      )
                                    }
                                    style={{
                                      width: "100%",
                                      minHeight: "60px",
                                      padding: "10px",
                                      fontSize: "12px",
                                      color: "#374151",
                                      backgroundColor: "#ffffff",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      resize: "vertical",
                                      fontFamily: "inherit",
                                      lineHeight: "1.5",
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}

                      {/* AI Recommended Vaccines */}
                      {aiRecommendations.recommendation.vaccines
                        .filter(shouldShowVaccine)
                        .map((vaccine, idx) => (
                          <div
                            key={idx}
                            style={{
                              backgroundColor: "#ffffff",
                              borderRadius: "10px",
                              border: "1px solid #e5e7eb",
                              borderLeft: "4px solid #14b8a6",
                              transition: "all 0.2s",
                              overflow: "hidden",
                            }}
                          >
                            {/* Collapsible Header */}
                            <div
                              onClick={() => toggleCard(`vaccine-${idx}`)}
                              style={{
                                padding: "12px 16px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: "8px",
                                cursor: "pointer",
                                backgroundColor: expandedCards[`vaccine-${idx}`]
                                  ? "#f9fafb"
                                  : "#ffffff",
                                transition: "background-color 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#f9fafb";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  expandedCards[`vaccine-${idx}`]
                                    ? "#f9fafb"
                                    : "#ffffff";
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                  flex: 1,
                                }}
                              >
                                <svg
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    color: "#14b8a6",
                                    transform: expandedCards[`vaccine-${idx}`]
                                      ? "rotate(90deg)"
                                      : "rotate(0deg)",
                                    transition: "transform 0.2s",
                                    flexShrink: 0,
                                  }}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                                <span
                                  style={{
                                    fontWeight: "700",
                                    color: "#1f2937",
                                    fontSize: "15px",
                                  }}
                                >
                                  {vaccine.vaccine_name}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <span
                                  style={{
                                    padding: "4px 10px",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    backgroundColor:
                                      vaccine.status === "RECOMMENDED"
                                        ? "#dcfce7"
                                        : vaccine.status === "CONTRAINDICATED"
                                          ? "#fee2e2"
                                          : "#fef3c7",
                                    color:
                                      vaccine.status === "RECOMMENDED"
                                        ? "#166534"
                                        : vaccine.status === "CONTRAINDICATED"
                                          ? "#991b1b"
                                          : "#92400e",
                                    border: `1px solid ${
                                      vaccine.status === "RECOMMENDED"
                                        ? "#86efac"
                                        : vaccine.status === "CONTRAINDICATED"
                                          ? "#fca5a5"
                                          : "#fde047"
                                    }`,
                                  }}
                                >
                                  {vaccine.status === "CONTRAINDICATED" &&
                                  vaccine.flags?.includes("REQUIRED_FOR_ENTRY")
                                    ? "Required for Entry"
                                    : vaccine.status}
                                </span>
                                {/* Yes/No Selection Buttons */}
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    onClick={() =>
                                      onSelectionChange(
                                        `ai-vaccine-${vaccine.protocol_id}`,
                                        true,
                                      )
                                    }
                                    style={{
                                      padding: "4px 10px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      backgroundColor:
                                        medicationSelections[
                                          `ai-vaccine-${vaccine.protocol_id}`
                                        ] === true
                                          ? "#dcfce7"
                                          : "white",
                                      color:
                                        medicationSelections[
                                          `ai-vaccine-${vaccine.protocol_id}`
                                        ] === true
                                          ? "#166534"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    onClick={() =>
                                      onSelectionChange(
                                        `ai-vaccine-${vaccine.protocol_id}`,
                                        false,
                                      )
                                    }
                                    style={{
                                      padding: "4px 10px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      backgroundColor:
                                        medicationSelections[
                                          `ai-vaccine-${vaccine.protocol_id}`
                                        ] === false
                                          ? "#fee2e2"
                                          : "white",
                                      color:
                                        medicationSelections[
                                          `ai-vaccine-${vaccine.protocol_id}`
                                        ] === false
                                          ? "#991b1b"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    No
                                  </button>
                                </div>
                                {/* Refills Dropdown */}
                                {onRefillInfoChange && onAdministrationSiteChange && (
                                  <RefillsDropdown
                                    medicationId={`ai-vaccine-${vaccine.protocol_id}`}
                                    refillInfo={refillInfos?.[`ai-vaccine-${vaccine.protocol_id}`] || vaccine.selectionDetails?.refillInfo || ""}
                                    administrationSite={administrationSites?.[`ai-vaccine-${vaccine.protocol_id}`] || vaccine.selectionDetails?.administrationSite || null}
                                    quantity={quantities?.[`ai-vaccine-${vaccine.protocol_id}`] || vaccine.selectionDetails?.quantity || ""}
                                    onRefillInfoChange={onRefillInfoChange}
                                    onAdministrationSiteChange={onAdministrationSiteChange}
                                    onQuantityChange={onQuantityChange}
                                    onSaveDetails={onSaveDetails}
                                    note={medicationNotes[`ai-vaccine-${vaccine.protocol_id}`] || vaccine.selectionDetails?.pharmacistNote || ""}
                                    isSaving={savingNotes[`ai-vaccine-${vaccine.protocol_id}`]}
                                    isVaccine={true}
                                    onRecordAdministration={handleRecordAdministration}
                                    isSelected={medicationSelections[`ai-vaccine-${vaccine.protocol_id}`] === true}
                                  />
                                )}
                              </div>
                            </div>

                            {/* Expandable Content */}
                            {expandedCards[`vaccine-${idx}`] && (
                              <div style={{ padding: "0 16px 16px 16px" }}>
                                {/* Quick Info Grid */}
                                <div
                                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                                  style={{
                                    gap: "8px",
                                    marginBottom: "12px",
                                    padding: "10px",
                                    backgroundColor: "#f9fafb",
                                    borderRadius: "8px",
                                  }}
                                >
                                  <div style={{ textAlign: "center" }}>
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "2px",
                                      }}
                                    >
                                      Dose
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "13px",
                                        color: "#1f2937",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {vaccine.dose}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      textAlign: "center",
                                      borderLeft: "1px solid #e5e7eb",
                                      borderRight: "1px solid #e5e7eb",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "2px",
                                      }}
                                    >
                                      Route
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "13px",
                                        color: "#1f2937",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {vaccine.route}
                                    </div>
                                  </div>
                                  <div style={{ textAlign: "center" }}>
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "2px",
                                      }}
                                    >
                                      Timing
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "13px",
                                        color: "#1f2937",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {vaccine.timing || "As scheduled"}
                                    </div>
                                  </div>
                                </div>

                                {/* Flags Warning */}
                                {vaccine.flags.length > 0 && (
                                  <div
                                    style={{
                                      padding: "10px 12px",
                                      backgroundColor: "#fef2f2",
                                      borderRadius: "8px",
                                      border: "1px solid #fecaca",
                                      display: "flex",
                                      alignItems: "flex-start",
                                      gap: "8px",
                                      marginBottom: "10px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#991b1b",
                                        fontWeight: "500",
                                      }}
                                    >
                                      {vaccine.flags.join(" | ")}
                                    </div>
                                  </div>
                                )}

                                {/* Selection Status Badge - Always visible */}
                                {vaccine.isSelected !== undefined && (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      marginBottom: "10px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        color: "#6b7280",
                                      }}
                                    >
                                      Pharmacist Selection:
                                    </span>
                                    <span
                                      style={{
                                        padding: "2px 8px",
                                        borderRadius: "4px",
                                        fontSize: "12px",
                                        fontWeight: "700",
                                        backgroundColor: vaccine.isSelected
                                          ? "#16a34a"
                                          : "#dc2626",
                                        color: "white",
                                      }}
                                    >
                                      {vaccine.isSelected ? "Yes" : "No"}
                                    </span>
                                  </div>
                                )}

                                {/* More Details - Teal Expandable */}
                                <button
                                  onClick={() =>
                                    toggleDetails(`vaccine-${idx}`)
                                  }
                                  style={{
                                    width: "100%",
                                    padding: "10px 14px",
                                    backgroundColor: "#ccfbf1",
                                    border: "1px solid #5eead4",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: "#0f766e",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "8px",
                                    transition: "all 0.2s",
                                    marginTop: "12px",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "#99f6e4";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "#ccfbf1";
                                  }}
                                >
                                  <span>More Details</span>
                                  <svg
                                    style={{
                                      width: "16px",
                                      height: "16px",
                                      transform: expandedDetails[
                                        `vaccine-${idx}`
                                      ]
                                        ? "rotate(180deg)"
                                        : "rotate(0deg)",
                                      transition: "transform 0.2s",
                                    }}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 9l-7 7-7-7"
                                    />
                                  </svg>
                                </button>

                                {/* Expandable Details */}
                                {expandedDetails[`vaccine-${idx}`] && (
                                  <div
                                    style={{
                                      marginTop: "8px",
                                      padding: "14px",
                                      backgroundColor: "#f9fafb",
                                      borderRadius: "8px",
                                      border: "1px solid #e5e7eb",
                                    }}
                                  >
                                    {vaccine.series_schedule && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Series Schedule
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#ffffff",
                                            padding: "8px 10px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                          }}
                                        >
                                          {vaccine.series_schedule}
                                        </div>
                                      </div>
                                    )}

                                    {vaccine.reasoning && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Clinical Reasoning
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#eff6ff",
                                            padding: "10px",
                                            borderRadius: "4px",
                                            border: "1px solid #dbeafe",
                                            lineHeight: "1.5",
                                          }}
                                        >
                                          {vaccine.reasoning}
                                        </div>
                                      </div>
                                    )}

                                    {vaccine.counseling_points.length > 0 && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "6px",
                                          }}
                                        >
                                          Counseling Points
                                        </div>
                                        <ul
                                          style={{
                                            margin: 0,
                                            paddingLeft: "20px",
                                            fontSize: "12px",
                                            color: "#374151",
                                          }}
                                        >
                                          {vaccine.counseling_points.map(
                                            (point, i) => (
                                              <li
                                                key={i}
                                                style={{
                                                  marginBottom: "4px",
                                                  lineHeight: "1.4",
                                                }}
                                              >
                                                {point}
                                              </li>
                                            ),
                                          )}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Pharmacist Note - Outside More Details but inside collapsible */}
                                <div style={{ marginTop: "12px" }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      Pharmacist Note
                                    </div>
                                    {onSaveNote && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSaveNote(
                                            `ai-vaccine-${vaccine.protocol_id}`,
                                          );
                                        }}
                                        disabled={
                                          savingNotes[
                                            `ai-vaccine-${vaccine.protocol_id}`
                                          ]
                                        }
                                        style={{
                                          padding: "2px 8px",
                                          fontSize: "10px",
                                          fontWeight: "600",
                                          color: savingNotes[
                                            `ai-vaccine-${vaccine.protocol_id}`
                                          ]
                                            ? "#9ca3af"
                                            : "#3b82f6",
                                          backgroundColor: "transparent",
                                          border: "1px solid",
                                          borderColor: savingNotes[
                                            `ai-vaccine-${vaccine.protocol_id}`
                                          ]
                                            ? "#d1d5db"
                                            : "#3b82f6",
                                          borderRadius: "4px",
                                          cursor: savingNotes[
                                            `ai-vaccine-${vaccine.protocol_id}`
                                          ]
                                            ? "not-allowed"
                                            : "pointer",
                                          transition: "all 0.2s",
                                        }}
                                      >
                                        {savingNotes[
                                          `ai-vaccine-${vaccine.protocol_id}`
                                        ]
                                          ? "Saving Note..."
                                          : "Save Note"}
                                      </button>
                                    )}
                                  </div>
                                  <textarea
                                    placeholder="Add notes for this vaccine..."
                                    value={
                                      medicationNotes[
                                        `ai-vaccine-${vaccine.protocol_id}`
                                      ] ||
                                      vaccine.selectionDetails
                                        ?.pharmacistNote ||
                                      ""
                                    }
                                    onChange={(e) =>
                                      onNoteChange(
                                        `ai-vaccine-${vaccine.protocol_id}`,
                                        e.target.value,
                                      )
                                    }
                                    style={{
                                      width: "100%",
                                      minHeight: "60px",
                                      padding: "10px",
                                      fontSize: "12px",
                                      color: "#374151",
                                      backgroundColor: "#ffffff",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      resize: "vertical",
                                      fontFamily: "inherit",
                                      lineHeight: "1.5",
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}

                      {/* NOT RECOMMENDED Vaccines - Displayed Inline */}
                      {(
                        aiRecommendations.recommendation.notRecommended
                          ?.vaccines || []
                      ).map((item, idx) => {
                        const displayName =
                          item.protocolMetadata?.displayName ||
                          item.vaccine_name;
                        const adultDose =
                          item.dose ||
                          item.protocolMetadata?.dosing?.adult?.dose;
                        const pediatricDosing =
                          item.protocolMetadata?.dosing?.pediatric;
                        const hasPediatricDosing =
                          Array.isArray(pediatricDosing) &&
                          pediatricDosing.length > 0;
                        // Get route from adult, or protocol metadata, or infer "injection" for vaccines
                        const route =
                          item.route ||
                          item.protocolMetadata?.dosing?.adult?.route ||
                          item.protocolMetadata?.route ||
                          (hasPediatricDosing ? "injection" : null);
                        const frequency =
                          item.protocolMetadata?.dosing?.adult?.frequency ||
                          (hasPediatricDosing && pediatricDosing[0]?.frequency);
                        return (
                          <div
                            key={`not-rec-vaccine-${idx}`}
                            style={{
                              backgroundColor: "#ffffff",
                              borderRadius: "10px",
                              border: "1px solid #fecaca",
                              borderLeft: "4px solid #dc2626",
                              transition: "all 0.2s",
                              overflow: "hidden",
                            }}
                          >
                            {/* Collapsible Header */}
                            <div
                              onClick={() =>
                                toggleCard(`not-rec-vaccine-${idx}`)
                              }
                              style={{
                                padding: "12px 16px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: "8px",
                                cursor: "pointer",
                                backgroundColor: expandedCards[
                                  `not-rec-vaccine-${idx}`
                                ]
                                  ? "#f9fafb"
                                  : "#ffffff",
                                transition: "background-color 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#f9fafb";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  expandedCards[`not-rec-vaccine-${idx}`]
                                    ? "#f9fafb"
                                    : "#ffffff";
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                  flex: 1,
                                }}
                              >
                                <svg
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    color: "#dc2626",
                                    transform: expandedCards[
                                      `not-rec-vaccine-${idx}`
                                    ]
                                      ? "rotate(90deg)"
                                      : "rotate(0deg)",
                                    transition: "transform 0.2s",
                                    flexShrink: 0,
                                  }}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                                <span
                                  style={{
                                    fontWeight: "700",
                                    color: "#991b1b",
                                    fontSize: "15px",
                                  }}
                                >
                                  {displayName}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <span
                                  style={{
                                    padding: "4px 10px",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    backgroundColor: "#fef2f2",
                                    color: "#dc2626",
                                    border: "1px solid #fecaca",
                                  }}
                                >
                                  NOT RECOMMENDED
                                </span>
                                {/* Yes/No Selection Buttons */}
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    onClick={() =>
                                      onSelectionChange(
                                        `not-rec-vaccine-${item.protocol_id}`,
                                        true,
                                      )
                                    }
                                    style={{
                                      padding: "4px 10px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      backgroundColor:
                                        medicationSelections[
                                          `not-rec-vaccine-${item.protocol_id}`
                                        ] === true
                                          ? "#dcfce7"
                                          : "white",
                                      color:
                                        medicationSelections[
                                          `not-rec-vaccine-${item.protocol_id}`
                                        ] === true
                                          ? "#166534"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    onClick={() =>
                                      onSelectionChange(
                                        `not-rec-vaccine-${item.protocol_id}`,
                                        false,
                                      )
                                    }
                                    style={{
                                      padding: "4px 10px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      backgroundColor:
                                        medicationSelections[
                                          `not-rec-vaccine-${item.protocol_id}`
                                        ] === false
                                          ? "#fee2e2"
                                          : "white",
                                      color:
                                        medicationSelections[
                                          `not-rec-vaccine-${item.protocol_id}`
                                        ] === false
                                          ? "#991b1b"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    No
                                  </button>
                                </div>
                                {/* Refills Dropdown */}
                                {onRefillInfoChange && onAdministrationSiteChange && (
                                  <RefillsDropdown
                                    medicationId={`not-rec-vaccine-${item.protocol_id}`}
                                    refillInfo={refillInfos?.[`not-rec-vaccine-${item.protocol_id}`] || ""}
                                    administrationSite={administrationSites?.[`not-rec-vaccine-${item.protocol_id}`] || null}
                                    quantity={quantities?.[`not-rec-vaccine-${item.protocol_id}`] || ""}
                                    onRefillInfoChange={onRefillInfoChange}
                                    onAdministrationSiteChange={onAdministrationSiteChange}
                                    onQuantityChange={onQuantityChange}
                                    onSaveDetails={onSaveDetails}
                                    note={medicationNotes[`not-rec-vaccine-${item.protocol_id}`] || ""}
                                    isSaving={savingNotes[`not-rec-vaccine-${item.protocol_id}`]}
                                    isVaccine={true}
                                    onRecordAdministration={handleRecordAdministration}
                                    isSelected={medicationSelections[`not-rec-vaccine-${item.protocol_id}`] === true}
                                  />
                                )}
                              </div>
                            </div>

                            {/* Expandable Content */}
                            {expandedCards[`not-rec-vaccine-${idx}`] && (
                              <div
                                style={{
                                  padding: "0 16px 16px 16px",
                                }}
                              >
                                {/* Quick Info Grid */}
                                {(adultDose ||
                                  hasPediatricDosing ||
                                  route ||
                                  frequency) && (
                                  <div
                                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                                    style={{
                                      gap: "8px",
                                      marginBottom: "12px",
                                      padding: "10px",
                                      backgroundColor: "#f9fafb",
                                      borderRadius: "8px",
                                    }}
                                  >
                                    <div style={{ textAlign: "center" }}>
                                      <div
                                        style={{
                                          fontSize: "10px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "2px",
                                        }}
                                      >
                                        Dosing
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#1f2937",
                                          fontWeight: "600",
                                          textAlign: "left",
                                        }}
                                      >
                                        {adultDose && (
                                          <div>Adult: {adultDose}</div>
                                        )}
                                        {hasPediatricDosing &&
                                          pediatricDosing.map(
                                            (ped: any, i: number) => (
                                              <div key={i}>
                                                {ped.age_range}: {ped.dose}
                                              </div>
                                            ),
                                          )}
                                        {!adultDose &&
                                          !hasPediatricDosing &&
                                          "N/A"}
                                      </div>
                                    </div>
                                    {route && (
                                      <div style={{ textAlign: "center" }}>
                                        <div
                                          style={{
                                            fontSize: "10px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "2px",
                                          }}
                                        >
                                          Route
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "13px",
                                            color: "#1f2937",
                                            fontWeight: "600",
                                          }}
                                        >
                                          {route}
                                        </div>
                                      </div>
                                    )}
                                    {frequency && (
                                      <div style={{ textAlign: "center" }}>
                                        <div
                                          style={{
                                            fontSize: "10px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "2px",
                                          }}
                                        >
                                          Frequency
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "13px",
                                            color: "#1f2937",
                                            fontWeight: "600",
                                          }}
                                        >
                                          {frequency}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Reason */}
                                {item.reasoning && (
                                  <div style={{ marginBottom: "12px" }}>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Reason
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#374151",
                                        backgroundColor: "#fef2f2",
                                        padding: "10px",
                                        borderRadius: "4px",
                                        border: "1px solid #fecaca",
                                        lineHeight: "1.5",
                                      }}
                                    >
                                      {item.reasoning}
                                    </div>
                                  </div>
                                )}

                                {/* Therapy Class */}
                                {(item.therapy_class ||
                                  item.protocolMetadata?.therapyClass) && (
                                  <div style={{ marginBottom: "12px" }}>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Therapy Class
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#374151",
                                        backgroundColor: "#ffffff",
                                        padding: "8px 10px",
                                        borderRadius: "4px",
                                        border: "1px solid #e5e7eb",
                                      }}
                                    >
                                      {item.therapy_class ||
                                        item.protocolMetadata?.therapyClass}
                                    </div>
                                  </div>
                                )}

                                {/* Counseling Points */}
                                {item.protocolMetadata?.counselingPoints &&
                                  item.protocolMetadata.counselingPoints
                                    .length > 0 && (
                                    <div style={{ marginBottom: "12px" }}>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Counseling Points
                                      </div>
                                      <ul
                                        style={{
                                          fontSize: "12px",
                                          color: "#374151",
                                          backgroundColor: "#fffbeb",
                                          padding: "10px 10px 10px 28px",
                                          borderRadius: "4px",
                                          border: "1px solid #fde68a",
                                          lineHeight: "1.6",
                                          margin: 0,
                                        }}
                                      >
                                        {item.protocolMetadata.counselingPoints.map(
                                          (point, pointIdx) => (
                                            <li
                                              key={pointIdx}
                                              style={{
                                                marginBottom:
                                                  pointIdx <
                                                  item.protocolMetadata!
                                                    .counselingPoints!.length -
                                                    1
                                                    ? "4px"
                                                    : 0,
                                              }}
                                            >
                                              {point}
                                            </li>
                                          ),
                                        )}
                                      </ul>
                                    </div>
                                  )}

                                {/* Contraindications - Allergies */}
                                {item.protocolMetadata?.contraindications
                                  ?.allergies &&
                                  item.protocolMetadata.contraindications
                                    .allergies.length > 0 && (
                                    <div style={{ marginBottom: "12px" }}>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Allergy Contraindications
                                      </div>
                                      <ul
                                        style={{
                                          fontSize: "12px",
                                          color: "#374151",
                                          backgroundColor: "#fef2f2",
                                          padding: "10px 10px 10px 28px",
                                          borderRadius: "4px",
                                          border: "1px solid #fecaca",
                                          lineHeight: "1.6",
                                          margin: 0,
                                        }}
                                      >
                                        {item.protocolMetadata.contraindications.allergies.map(
                                          (allergy, allergyIdx) => (
                                            <li
                                              key={allergyIdx}
                                              style={{
                                                marginBottom:
                                                  allergyIdx <
                                                  item.protocolMetadata!
                                                    .contraindications!
                                                    .allergies!.length -
                                                    1
                                                    ? "4px"
                                                    : 0,
                                              }}
                                            >
                                              {allergy}
                                            </li>
                                          ),
                                        )}
                                      </ul>
                                    </div>
                                  )}

                                {/* Contraindications - Conditions */}
                                {item.protocolMetadata?.contraindications
                                  ?.conditions &&
                                  item.protocolMetadata.contraindications
                                    .conditions.length > 0 && (
                                    <div style={{ marginBottom: "12px" }}>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Condition Contraindications
                                      </div>
                                      <ul
                                        style={{
                                          fontSize: "12px",
                                          color: "#374151",
                                          backgroundColor: "#fef2f2",
                                          padding: "10px 10px 10px 28px",
                                          borderRadius: "4px",
                                          border: "1px solid #fecaca",
                                          lineHeight: "1.6",
                                          margin: 0,
                                        }}
                                      >
                                        {item.protocolMetadata.contraindications.conditions.map(
                                          (condition, conditionIdx) => (
                                            <li
                                              key={conditionIdx}
                                              style={{
                                                marginBottom:
                                                  conditionIdx <
                                                  item.protocolMetadata!
                                                    .contraindications!
                                                    .conditions!.length -
                                                    1
                                                    ? "4px"
                                                    : 0,
                                              }}
                                            >
                                              {condition}
                                            </li>
                                          ),
                                        )}
                                      </ul>
                                    </div>
                                  )}

                                {/* Notes */}
                                {item.protocolMetadata?.notes && (
                                  <div style={{ marginBottom: "12px" }}>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Notes
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#374151",
                                        backgroundColor: "#f3f4f6",
                                        padding: "10px",
                                        borderRadius: "4px",
                                        border: "1px solid #e5e7eb",
                                        lineHeight: "1.5",
                                      }}
                                    >
                                      {item.protocolMetadata.notes}
                                    </div>
                                  </div>
                                )}

                                {/* More Details - Red Expandable */}
                                <button
                                  onClick={() =>
                                    toggleDetails(`not-rec-vaccine-${idx}`)
                                  }
                                  style={{
                                    width: "100%",
                                    padding: "10px 14px",
                                    backgroundColor: "#fee2e2",
                                    border: "1px solid #fecaca",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: "#991b1b",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "8px",
                                    transition: "all 0.2s",
                                    marginTop: "12px",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "#fecaca";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "#fee2e2";
                                  }}
                                >
                                  <span>More Details</span>
                                  <svg
                                    style={{
                                      width: "16px",
                                      height: "16px",
                                      transform: expandedDetails[
                                        `not-rec-vaccine-${idx}`
                                      ]
                                        ? "rotate(180deg)"
                                        : "rotate(0deg)",
                                      transition: "transform 0.2s",
                                    }}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 9l-7 7-7-7"
                                    />
                                  </svg>
                                </button>

                                {/* Expandable Details */}
                                {expandedDetails[`not-rec-vaccine-${idx}`] && (
                                  <div
                                    style={{
                                      marginTop: "8px",
                                      padding: "14px",
                                      backgroundColor: "#fef2f2",
                                      borderRadius: "8px",
                                      border: "1px solid #fecaca",
                                    }}
                                  >
                                    {/* Protocol ID */}
                                    {item.protocol_id && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Protocol ID
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#ffffff",
                                            padding: "8px 10px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                          }}
                                        >
                                          {item.protocol_id}
                                        </div>
                                      </div>
                                    )}

                                    {/* Timing Information */}
                                    {item.timing && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Timing
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#ffffff",
                                            padding: "8px 10px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                          }}
                                        >
                                          {item.timing}
                                        </div>
                                      </div>
                                    )}

                                    {/* Protocol Timing */}
                                    {item.protocolMetadata?.timing && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Timing Details
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#ffffff",
                                            padding: "8px 10px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                            lineHeight: "1.6",
                                          }}
                                        >
                                          {item.protocolMetadata.timing
                                            .minimum_days_before_travel && (
                                            <div>
                                              <strong>
                                                Minimum days before travel:
                                              </strong>{" "}
                                              {
                                                item.protocolMetadata.timing
                                                  .minimum_days_before_travel
                                              }
                                            </div>
                                          )}
                                          {item.protocolMetadata.timing
                                            .booster_interval && (
                                            <div>
                                              <strong>Booster interval:</strong>{" "}
                                              {
                                                item.protocolMetadata.timing
                                                  .booster_interval
                                              }
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Full Dosing Information */}
                                    {item.protocolMetadata?.dosing && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Full Dosing Information
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#ffffff",
                                            padding: "10px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                            lineHeight: "1.6",
                                          }}
                                        >
                                          {item.protocolMetadata.dosing
                                            .adult && (
                                            <div
                                              style={{ marginBottom: "8px" }}
                                            >
                                              <strong>Adult:</strong>
                                              <div
                                                style={{
                                                  marginLeft: "12px",
                                                  marginTop: "4px",
                                                }}
                                              >
                                                {item.protocolMetadata.dosing
                                                  .adult.dose && (
                                                  <div>
                                                    Dose:{" "}
                                                    {
                                                      item.protocolMetadata
                                                        .dosing.adult.dose
                                                    }
                                                  </div>
                                                )}
                                                {item.protocolMetadata.dosing
                                                  .adult.route && (
                                                  <div>
                                                    Route:{" "}
                                                    {
                                                      item.protocolMetadata
                                                        .dosing.adult.route
                                                    }
                                                  </div>
                                                )}
                                                {item.protocolMetadata.dosing
                                                  .adult.frequency && (
                                                  <div>
                                                    Frequency:{" "}
                                                    {
                                                      item.protocolMetadata
                                                        .dosing.adult.frequency
                                                    }
                                                  </div>
                                                )}
                                                {item.protocolMetadata.dosing
                                                  .adult.instructions && (
                                                  <div>
                                                    Instructions:{" "}
                                                    {
                                                      item.protocolMetadata
                                                        .dosing.adult
                                                        .instructions
                                                    }
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                          {item.protocolMetadata.dosing
                                            .pediatric &&
                                            item.protocolMetadata.dosing
                                              .pediatric.length > 0 && (
                                              <div>
                                                <strong>Pediatric:</strong>
                                                {item.protocolMetadata.dosing.pediatric.map(
                                                  (ped, pedIdx) => (
                                                    <div
                                                      key={pedIdx}
                                                      style={{
                                                        marginLeft: "12px",
                                                        marginTop: "4px",
                                                        paddingLeft: "8px",
                                                        borderLeft:
                                                          "2px solid #e5e7eb",
                                                      }}
                                                    >
                                                      {ped.age_range && (
                                                        <div>
                                                          <strong>
                                                            {ped.age_range}:
                                                          </strong>
                                                        </div>
                                                      )}
                                                      {ped.dose && (
                                                        <div>
                                                          Dose: {ped.dose}
                                                        </div>
                                                      )}
                                                      {ped.frequency && (
                                                        <div>
                                                          Frequency:{" "}
                                                          {ped.frequency}
                                                        </div>
                                                      )}
                                                      {ped.instructions && (
                                                        <div>
                                                          Instructions:{" "}
                                                          {ped.instructions}
                                                        </div>
                                                      )}
                                                    </div>
                                                  ),
                                                )}
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Flags */}
                                    {item.flags && item.flags.length > 0 && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Flags
                                        </div>
                                        <div
                                          style={{
                                            display: "flex",
                                            gap: "6px",
                                            flexWrap: "wrap",
                                          }}
                                        >
                                          {item.flags.map((flag, flagIdx) => (
                                            <span
                                              key={flagIdx}
                                              style={{
                                                fontSize: "11px",
                                                padding: "4px 8px",
                                                backgroundColor: "#fef3c7",
                                                color: "#92400e",
                                                borderRadius: "4px",
                                                border: "1px solid #fde68a",
                                                fontWeight: "500",
                                              }}
                                            >
                                              {flag}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Status */}
                                    {item.status && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Status
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#ffffff",
                                            padding: "8px 10px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                          }}
                                        >
                                          {item.status}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Pharmacist Note - Outside More Details but inside collapsible */}
                                <div style={{ marginTop: "12px" }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      Pharmacist Note
                                    </div>
                                    {onSaveNote && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSaveNote(
                                            `not-rec-vaccine-${item.protocol_id}`,
                                          );
                                        }}
                                        disabled={
                                          savingNotes[
                                            `not-rec-vaccine-${item.protocol_id}`
                                          ]
                                        }
                                        style={{
                                          padding: "2px 8px",
                                          fontSize: "10px",
                                          fontWeight: "600",
                                          color: savingNotes[
                                            `not-rec-vaccine-${item.protocol_id}`
                                          ]
                                            ? "#9ca3af"
                                            : "#3b82f6",
                                          backgroundColor: "transparent",
                                          border: "1px solid",
                                          borderColor: savingNotes[
                                            `not-rec-vaccine-${item.protocol_id}`
                                          ]
                                            ? "#d1d5db"
                                            : "#3b82f6",
                                          borderRadius: "4px",
                                          cursor: savingNotes[
                                            `not-rec-vaccine-${item.protocol_id}`
                                          ]
                                            ? "not-allowed"
                                            : "pointer",
                                          transition: "all 0.2s",
                                        }}
                                      >
                                        {savingNotes[
                                          `not-rec-vaccine-${item.protocol_id}`
                                        ]
                                          ? "Saving Note..."
                                          : "Save Note"}
                                      </button>
                                    )}
                                  </div>
                                  <textarea
                                    placeholder="Add notes for this vaccine..."
                                    value={
                                      medicationNotes[
                                        `not-rec-vaccine-${item.protocol_id}`
                                      ] || ""
                                    }
                                    onChange={(e) =>
                                      onNoteChange(
                                        `not-rec-vaccine-${item.protocol_id}`,
                                        e.target.value,
                                      )
                                    }
                                    style={{
                                      width: "100%",
                                      minHeight: "60px",
                                      padding: "10px",
                                      fontSize: "12px",
                                      color: "#374151",
                                      backgroundColor: "#ffffff",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      resize: "vertical",
                                      fontFamily: "inherit",
                                      lineHeight: "1.5",
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Medications Section - Collapsible */}
            {(aiRecommendations.recommendation.medications.filter(
              (m) => m.status === "RECOMMENDED" || m.status === "CONDITIONAL",
            ).length > 0 ||
              (aiRecommendations.recommendation.notRecommended?.medications || []).length > 0 ||
              (aiRecommendations.recommendation.pharmacistAddedMedications || []).length > 0 ||
              manualSelections.filter((s) => s.type === "medication").length > 0) && (
              <div
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                  border: "1px solid #e5e7eb",
                }}
              >
                {/* Collapsible Header */}
                <div
                  onClick={() => setMedicationsExpanded(!medicationsExpanded)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "8px",
                    padding: "16px 20px",
                    backgroundColor: "#fef3c7",
                    borderBottom: medicationsExpanded
                      ? "1px solid #e5e7eb"
                      : "none",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#fde68a";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#fef3c7";
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <svg
                      style={{
                        width: "18px",
                        height: "18px",
                        transform: medicationsExpanded
                          ? "rotate(0deg)"
                          : "rotate(-90deg)",
                        transition: "transform 0.2s",
                        color: "#d97706",
                      }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                    <h4
                      style={{
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#d97706",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      Medications
                    </h4>
                    <span
                      style={{
                        padding: "4px 10px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: "700",
                        backgroundColor: "#f59e0b",
                        color: "white",
                      }}
                    >
                      {aiRecommendations.recommendation.medications.filter(
                        (m) =>
                          m.status === "RECOMMENDED" ||
                          m.status === "CONDITIONAL",
                      ).length +
                        (aiRecommendations.recommendation.notRecommended
                          ?.medications?.length || 0) +
                        (
                          aiRecommendations.recommendation
                            .pharmacistAddedMedications || []
                        ).filter((m) => {
                          const selectionKey = `manual-medication-${m.protocol_id}`;
                          // Exclude medications that exist in NOT_RECOMMENDED list
                          const notRecMeds =
                            aiRecommendations.recommendation.notRecommended
                              ?.medications || [];
                          const existsInNotRec = notRecMeds.some(
                            (nrm) => nrm.protocol_id === m.protocol_id,
                          );
                          return (
                            medicationSelections[selectionKey] !== false &&
                            !existsInNotRec
                          );
                        }).length +
                        // Include current session manual medication selections
                        manualSelections
                          .filter((s) => s.type === "medication")
                          .filter(
                            (s) =>
                              !(
                                aiRecommendations.recommendation
                                  .pharmacistAddedMedications || []
                              ).some(
                                (m) => m.protocol_id === s.protocol.protocolId,
                              ),
                          )
                          .filter((s) => {
                            const selectionKey = `manual-medication-${s.protocol.protocolId}`;
                            return medicationSelections[selectionKey] !== false;
                          }).length}
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      fontWeight: "500",
                    }}
                  >
                    {medicationsExpanded
                      ? "Click to collapse"
                      : "Click to expand"}
                  </span>
                </div>

                {/* Medications Content */}
                {medicationsExpanded && (
                  <div style={{ padding: "16px" }}>
                    <div
                      style={{
                        display: "grid",
                        gap: "12px",
                      }}
                    >
                      {/* Pharmacist Added Medications */}
                      {(
                        aiRecommendations.recommendation
                          .pharmacistAddedMedications || []
                      )
                        .filter((medication) => {
                          // Exclude medications that exist in NOT_RECOMMENDED list
                          const notRecMeds =
                            aiRecommendations.recommendation.notRecommended
                              ?.medications || [];
                          return !notRecMeds.some(
                            (nrm) => nrm.protocol_id === medication.protocol_id,
                          );
                        })
                        .map((medication, idx) => (
                          <div
                            key={`pharmacist-medication-${idx}`}
                            style={{
                              backgroundColor: "#ffffff",
                              borderRadius: "10px",
                              border: "1px solid #e5e7eb",
                              borderLeft: "4px solid #f59e0b",
                              transition: "all 0.2s",
                              overflow: "hidden",
                            }}
                          >
                            {/* Collapsible Header */}
                            <div
                              onClick={() =>
                                toggleCard(`pharmacist-medication-${idx}`)
                              }
                              style={{
                                padding: "12px 16px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: "8px",
                                cursor: "pointer",
                                backgroundColor: expandedCards[
                                  `pharmacist-medication-${idx}`
                                ]
                                  ? "#f9fafb"
                                  : "#ffffff",
                                transition: "background-color 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#f9fafb";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  expandedCards[`pharmacist-medication-${idx}`]
                                    ? "#f9fafb"
                                    : "#ffffff";
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                  flex: 1,
                                }}
                              >
                                <svg
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    color: "#f59e0b",
                                    transform: expandedCards[
                                      `pharmacist-medication-${idx}`
                                    ]
                                      ? "rotate(90deg)"
                                      : "rotate(0deg)",
                                    transition: "transform 0.2s",
                                    flexShrink: 0,
                                  }}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                                <span
                                  style={{
                                    fontWeight: "700",
                                    color: "#1f2937",
                                    fontSize: "15px",
                                  }}
                                >
                                  {medication.name}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <span
                                  style={{
                                    padding: "4px 10px",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    backgroundColor: "#fef3c7",
                                    color: "#92400e",
                                    border: "1px solid #fde047",
                                  }}
                                >
                                  PHARMACIST ADDED
                                </span>
                                {/* Yes/No Selection Buttons */}
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    onClick={() =>
                                      handleManualSelectionChange(
                                        medication.protocol_id,
                                        "medication",
                                        true,
                                      )
                                    }
                                    style={{
                                      padding: "4px 10px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      backgroundColor:
                                        medicationSelections[
                                          `manual-medication-${medication.protocol_id}`
                                        ] === true
                                          ? "#dcfce7"
                                          : "white",
                                      color:
                                        medicationSelections[
                                          `manual-medication-${medication.protocol_id}`
                                        ] === true
                                          ? "#166534"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleManualSelectionChange(
                                        medication.protocol_id,
                                        "medication",
                                        false,
                                      )
                                    }
                                    style={{
                                      padding: "4px 10px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      backgroundColor:
                                        medicationSelections[
                                          `manual-medication-${medication.protocol_id}`
                                        ] === false
                                          ? "#fee2e2"
                                          : "white",
                                      color:
                                        medicationSelections[
                                          `manual-medication-${medication.protocol_id}`
                                        ] === false
                                          ? "#991b1b"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    No
                                  </button>
                                </div>
                                {/* Refills Dropdown */}
                                {onRefillInfoChange && onAdministrationSiteChange && (
                                  <RefillsDropdown
                                    medicationId={`manual-medication-${medication.protocol_id}`}
                                    refillInfo={refillInfos?.[`manual-medication-${medication.protocol_id}`] || medication.selectionDetails?.refillInfo || ""}
                                    administrationSite={administrationSites?.[`manual-medication-${medication.protocol_id}`] || medication.selectionDetails?.administrationSite || null}
                                    quantity={quantities?.[`manual-medication-${medication.protocol_id}`] || medication.selectionDetails?.quantity || ""}
                                    onRefillInfoChange={onRefillInfoChange}
                                    onAdministrationSiteChange={onAdministrationSiteChange}
                                    onQuantityChange={onQuantityChange}
                                    onSaveDetails={onSaveDetails}
                                    note={medicationNotes[`manual-medication-${medication.protocol_id}`] || medication.selectionDetails?.pharmacistNote || ""}
                                    isSaving={savingNotes[`manual-medication-${medication.protocol_id}`]}
                                    isVaccine={false}
                                    isSelected={medicationSelections[`manual-medication-${medication.protocol_id}`] === true}
                                  />
                                )}
                              </div>
                            </div>

                            {/* Expandable Content */}
                            {expandedCards[`pharmacist-medication-${idx}`] && (
                              <div style={{ padding: "0 16px 16px 16px" }}>
                                {/* Quick Info Grid */}
                                <div
                                  className="grid grid-cols-1 sm:grid-cols-2"
                                  style={{
                                    marginBottom: "12px",
                                    gap: "8px",
                                    padding: "10px",
                                    backgroundColor: "#f9fafb",
                                    borderRadius: "8px",
                                  }}
                                >
                                  <div style={{ textAlign: "center" }}>
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "2px",
                                      }}
                                    >
                                      Dosing
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#1f2937",
                                        fontWeight: "600",
                                        textAlign: "left",
                                      }}
                                    >
                                      {medication.protocolMetadata?.dosing?.adult
                                        ?.dose && (
                                        <div>
                                          Adult:{" "}
                                          {
                                            medication.protocolMetadata.dosing
                                              .adult.dose
                                          }
                                        </div>
                                      )}
                                      {medication.protocolMetadata?.dosing
                                        ?.pediatric &&
                                        Array.isArray(
                                          medication.protocolMetadata.dosing
                                            .pediatric,
                                        ) &&
                                        medication.protocolMetadata.dosing
                                          .pediatric.length > 0 &&
                                        medication.protocolMetadata.dosing.pediatric.map(
                                          (ped: any, i: number) => (
                                            <div key={i}>
                                              {ped.age_range}: {ped.dose}
                                            </div>
                                          ),
                                        )}
                                      {!medication.protocolMetadata?.dosing?.adult
                                        ?.dose &&
                                        (!medication.protocolMetadata?.dosing
                                          ?.pediatric ||
                                          medication.protocolMetadata.dosing
                                            .pediatric.length === 0) &&
                                        (medication.doses || "N/A")}
                                    </div>
                                  </div>
                                  <div style={{ textAlign: "center" }}>
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "2px",
                                      }}
                                    >
                                      Route
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "13px",
                                        color: "#1f2937",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {medication.protocolMetadata?.dosing?.adult
                                        ?.route || medication.route}
                                    </div>
                                  </div>
                                </div>
                                {/* Timing Information */}
                                {medication.protocolMetadata?.timing &&
                                  (medication.protocolMetadata.timing
                                    .minimum_days_before_travel ||
                                  medication.protocolMetadata.timing
                                    .booster_interval ||
                                  medication.protocolMetadata.timing
                                    .start_days_before ||
                                  medication.protocolMetadata.timing
                                    .days_after_leaving ? (
                                    <div
                                      style={{
                                        marginBottom: "12px",
                                        padding: "10px",
                                        backgroundColor: "#fef3c7",
                                        borderRadius: "8px",
                                        border: "1px solid #fde047",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "10px",
                                          color: "#92400e",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "8px",
                                        }}
                                      >
                                        Timing Information
                                      </div>
                                      {medication.protocolMetadata.timing
                                        .start_days_before && (
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#78350f",
                                            marginBottom: "6px",
                                          }}
                                        >
                                          <span style={{ fontWeight: "600" }}>
                                            Start:
                                          </span>{" "}
                                          {
                                            medication.protocolMetadata.timing
                                              .start_days_before
                                          }{" "}
                                          days before travel
                                        </div>
                                      )}
                                      {medication.protocolMetadata.timing
                                        .days_after_leaving && (
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#78350f",
                                            marginBottom: "6px",
                                          }}
                                        >
                                          <span style={{ fontWeight: "600" }}>
                                            Continue:
                                          </span>{" "}
                                          {
                                            medication.protocolMetadata.timing
                                              .days_after_leaving
                                          }{" "}
                                          days after leaving
                                        </div>
                                      )}
                                      {medication.protocolMetadata.timing
                                        .minimum_days_before_travel && (
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#78350f",
                                            marginBottom: "6px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                          }}
                                        >
                                          <span style={{ fontWeight: "600" }}>
                                            Minimum days before travel:
                                          </span>
                                          <span style={{ fontWeight: "700" }}>
                                            {
                                              medication.protocolMetadata.timing
                                                .minimum_days_before_travel
                                            }{" "}
                                            days
                                          </span>
                                        </div>
                                      )}
                                      {medication.protocolMetadata.timing
                                        .booster_interval && (
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#78350f",
                                            lineHeight: "1.5",
                                          }}
                                        >
                                          <span style={{ fontWeight: "600" }}>
                                            Booster:
                                          </span>{" "}
                                          {
                                            medication.protocolMetadata.timing
                                              .booster_interval
                                          }
                                        </div>
                                      )}
                                    </div>
                                  ) : null)}

                                {/* Brands */}
                                {medication.brands &&
                                  medication.brands.length > 0 && (
                                    <div
                                      style={{
                                        marginBottom: "12px",
                                        padding: "10px",
                                        backgroundColor: "#f9fafb",
                                        borderRadius: "8px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "10px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Brands
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#1f2937",
                                        }}
                                      >
                                        {medication.brands.join(", ")}
                                      </div>
                                    </div>
                                  )}

                                {/* Age Group */}
                                {medication.age_group && (
                                  <div style={{ marginBottom: "12px" }}>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Age Group
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#374151",
                                        backgroundColor: "#ffffff",
                                        padding: "8px 10px",
                                        borderRadius: "4px",
                                        border: "1px solid #e5e7eb",
                                      }}
                                    >
                                      {medication.age_group}
                                    </div>
                                  </div>
                                )}

                                {/* Therapy Class */}
                                {medication.therapy_class && (
                                  <div style={{ marginBottom: "12px" }}>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Therapy Class
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#374151",
                                        backgroundColor: "#ffffff",
                                        padding: "8px 10px",
                                        borderRadius: "4px",
                                        border: "1px solid #e5e7eb",
                                      }}
                                    >
                                      {medication.therapy_class}
                                    </div>
                                  </div>
                                )}

                                {/* Notes from Protocol */}
                                {medication.notes && (
                                  <div style={{ marginBottom: "12px" }}>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Protocol Notes
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#374151",
                                        backgroundColor: "#eff6ff",
                                        padding: "10px",
                                        borderRadius: "4px",
                                        border: "1px solid #dbeafe",
                                        lineHeight: "1.5",
                                      }}
                                    >
                                      {medication.notes}
                                    </div>
                                  </div>
                                )}

                                {/* Counseling Points */}
                                {medication.protocolMetadata
                                  ?.counselingPoints &&
                                  medication.protocolMetadata.counselingPoints
                                    .length > 0 && (
                                    <div style={{ marginBottom: "12px" }}>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Counseling Points
                                      </div>
                                      <ul
                                        style={{
                                          fontSize: "12px",
                                          color: "#374151",
                                          backgroundColor: "#fffbeb",
                                          padding: "10px 10px 10px 28px",
                                          borderRadius: "4px",
                                          border: "1px solid #fde68a",
                                          lineHeight: "1.6",
                                          margin: 0,
                                        }}
                                      >
                                        {medication.protocolMetadata.counselingPoints.map(
                                          (point: string, pointIdx: number) => (
                                            <li
                                              key={pointIdx}
                                              style={{
                                                marginBottom:
                                                  pointIdx <
                                                  medication.protocolMetadata!
                                                    .counselingPoints!.length -
                                                    1
                                                    ? "4px"
                                                    : 0,
                                              }}
                                            >
                                              {point}
                                            </li>
                                          ),
                                        )}
                                      </ul>
                                    </div>
                                  )}

                                {/* Contraindications - Allergies */}
                                {medication.protocolMetadata?.contraindications
                                  ?.allergies &&
                                  medication.protocolMetadata.contraindications
                                    .allergies.length > 0 && (
                                    <div style={{ marginBottom: "12px" }}>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Allergy Contraindications
                                      </div>
                                      <ul
                                        style={{
                                          fontSize: "12px",
                                          color: "#374151",
                                          backgroundColor: "#fef2f2",
                                          padding: "10px 10px 10px 28px",
                                          borderRadius: "4px",
                                          border: "1px solid #fecaca",
                                          lineHeight: "1.6",
                                          margin: 0,
                                        }}
                                      >
                                        {medication.protocolMetadata.contraindications.allergies.map(
                                          (
                                            allergy: string,
                                            allergyIdx: number,
                                          ) => (
                                            <li
                                              key={allergyIdx}
                                              style={{
                                                marginBottom:
                                                  allergyIdx <
                                                  medication.protocolMetadata!
                                                    .contraindications!
                                                    .allergies!.length -
                                                    1
                                                    ? "4px"
                                                    : 0,
                                              }}
                                            >
                                              {allergy}
                                            </li>
                                          ),
                                        )}
                                      </ul>
                                    </div>
                                  )}

                                {/* Contraindications - Conditions */}
                                {medication.protocolMetadata?.contraindications
                                  ?.conditions &&
                                  medication.protocolMetadata.contraindications
                                    .conditions.length > 0 && (
                                    <div style={{ marginBottom: "12px" }}>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Condition Contraindications
                                      </div>
                                      <ul
                                        style={{
                                          fontSize: "12px",
                                          color: "#374151",
                                          backgroundColor: "#fef2f2",
                                          padding: "10px 10px 10px 28px",
                                          borderRadius: "4px",
                                          border: "1px solid #fecaca",
                                          lineHeight: "1.6",
                                          margin: 0,
                                        }}
                                      >
                                        {medication.protocolMetadata.contraindications.conditions.map(
                                          (
                                            condition: string,
                                            conditionIdx: number,
                                          ) => (
                                            <li
                                              key={conditionIdx}
                                              style={{
                                                marginBottom:
                                                  conditionIdx <
                                                  medication.protocolMetadata!
                                                    .contraindications!
                                                    .conditions!.length -
                                                    1
                                                    ? "4px"
                                                    : 0,
                                              }}
                                            >
                                              {condition}
                                            </li>
                                          ),
                                        )}
                                      </ul>
                                    </div>
                                  )}

                                {/* More Details Button - Teal Styled */}
                                <button
                                  onClick={() =>
                                    toggleDetails(
                                      `pharmacist-medication-${idx}`,
                                    )
                                  }
                                  style={{
                                    width: "100%",
                                    padding: "10px 14px",
                                    backgroundColor: "#ccfbf1",
                                    border: "1px solid #5eead4",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: "#0d9488",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "8px",
                                    transition: "all 0.2s",
                                    marginTop: "12px",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "#99f6e4";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "#ccfbf1";
                                  }}
                                >
                                  <span>More Details</span>
                                  <svg
                                    style={{
                                      width: "16px",
                                      height: "16px",
                                      transform: expandedDetails[
                                        `pharmacist-medication-${idx}`
                                      ]
                                        ? "rotate(180deg)"
                                        : "rotate(0deg)",
                                      transition: "transform 0.2s",
                                    }}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 9l-7 7-7-7"
                                    />
                                  </svg>
                                </button>

                                {/* Expandable More Details */}
                                {expandedDetails[
                                  `pharmacist-medication-${idx}`
                                ] && (
                                  <div
                                    style={{
                                      marginTop: "8px",
                                      padding: "14px",
                                      backgroundColor: "#f0fdfa",
                                      borderRadius: "8px",
                                      border: "1px solid #99f6e4",
                                    }}
                                  >
                                    {/* Protocol ID */}
                                    <div style={{ marginBottom: "12px" }}>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Protocol ID
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#374151",
                                          backgroundColor: "#ffffff",
                                          padding: "8px 10px",
                                          borderRadius: "4px",
                                          border: "1px solid #e5e7eb",
                                        }}
                                      >
                                        {medication.protocol_id}
                                      </div>
                                    </div>

                                    {/* Timing Information */}
                                    {medication.protocolMetadata?.timing && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Timing Details
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#ffffff",
                                            padding: "8px 10px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                            lineHeight: "1.6",
                                          }}
                                        >
                                          {(
                                            medication.protocolMetadata
                                              .timing as any
                                          ).start_days_before && (
                                            <div>
                                              <strong>
                                                Start days before travel:
                                              </strong>{" "}
                                              {
                                                (
                                                  medication.protocolMetadata
                                                    .timing as any
                                                ).start_days_before
                                              }
                                            </div>
                                          )}
                                          {(
                                            medication.protocolMetadata
                                              .timing as any
                                          ).days_after_leaving && (
                                            <div>
                                              <strong>
                                                Days after leaving:
                                              </strong>{" "}
                                              {
                                                (
                                                  medication.protocolMetadata
                                                    .timing as any
                                                ).days_after_leaving
                                              }
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Full Dosing Information */}
                                    {medication.protocolMetadata?.dosing && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Full Dosing Information
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#ffffff",
                                            padding: "10px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                            lineHeight: "1.6",
                                          }}
                                        >
                                          {medication.protocolMetadata.dosing
                                            .adult && (
                                            <div
                                              style={{ marginBottom: "8px" }}
                                            >
                                              <strong>Adult:</strong>
                                              <div
                                                style={{
                                                  marginLeft: "12px",
                                                  marginTop: "4px",
                                                }}
                                              >
                                                {medication.protocolMetadata
                                                  .dosing.adult.dose && (
                                                  <div>
                                                    Dose:{" "}
                                                    {
                                                      medication
                                                        .protocolMetadata.dosing
                                                        .adult.dose
                                                    }
                                                  </div>
                                                )}
                                                {medication.protocolMetadata
                                                  .dosing.adult.route && (
                                                  <div>
                                                    Route:{" "}
                                                    {
                                                      medication
                                                        .protocolMetadata.dosing
                                                        .adult.route
                                                    }
                                                  </div>
                                                )}
                                                {medication.protocolMetadata
                                                  .dosing.adult.frequency && (
                                                  <div>
                                                    Frequency:{" "}
                                                    {
                                                      medication
                                                        .protocolMetadata.dosing
                                                        .adult.frequency
                                                    }
                                                  </div>
                                                )}
                                                {medication.protocolMetadata
                                                  .dosing.adult
                                                  .instructions && (
                                                  <div>
                                                    Instructions:{" "}
                                                    {
                                                      medication
                                                        .protocolMetadata.dosing
                                                        .adult.instructions
                                                    }
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                          {medication.protocolMetadata.dosing
                                            .pediatric &&
                                            medication.protocolMetadata.dosing
                                              .pediatric.length > 0 && (
                                              <div>
                                                <strong>Pediatric:</strong>
                                                {medication.protocolMetadata.dosing.pediatric.map(
                                                  (
                                                    ped: any,
                                                    pedIdx: number,
                                                  ) => (
                                                    <div
                                                      key={pedIdx}
                                                      style={{
                                                        marginLeft: "12px",
                                                        marginTop: "4px",
                                                        paddingLeft: "8px",
                                                        borderLeft:
                                                          "2px solid #e5e7eb",
                                                      }}
                                                    >
                                                      {ped.age_range && (
                                                        <div>
                                                          <strong>
                                                            {ped.age_range}:
                                                          </strong>
                                                        </div>
                                                      )}
                                                      {ped.dose && (
                                                        <div>
                                                          Dose: {ped.dose}
                                                        </div>
                                                      )}
                                                      {ped.frequency && (
                                                        <div>
                                                          Frequency:{" "}
                                                          {ped.frequency}
                                                        </div>
                                                      )}
                                                      {ped.instructions && (
                                                        <div>
                                                          Instructions:{" "}
                                                          {ped.instructions}
                                                        </div>
                                                      )}
                                                    </div>
                                                  ),
                                                )}
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Quantity */}
                                    {medication.protocolMetadata?.quantity && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Quantity
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#ffffff",
                                            padding: "8px 10px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                          }}
                                        >
                                          {medication.protocolMetadata.quantity}
                                        </div>
                                      </div>
                                    )}

                                    {/* Protocol Type */}
                                    {medication.protocolMetadata
                                      ?.protocolType && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Protocol Type
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#ffffff",
                                            padding: "8px 10px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                          }}
                                        >
                                          {
                                            medication.protocolMetadata
                                              .protocolType
                                          }
                                        </div>
                                      </div>
                                    )}

                                    {/* Eligibility - Pregnancy/Breastfeeding */}
                                    {medication.protocolMetadata?.eligibility &&
                                      (medication.protocolMetadata.eligibility
                                        .pregnancy ||
                                        medication.protocolMetadata.eligibility
                                          .breastfeeding ||
                                        medication.protocolMetadata.eligibility
                                          .min_age_years ||
                                        medication.protocolMetadata.eligibility
                                          .max_age_years) && (
                                        <div style={{ marginBottom: "12px" }}>
                                          <div
                                            style={{
                                              fontSize: "11px",
                                              color: "#6b7280",
                                              fontWeight: "600",
                                              textTransform: "uppercase",
                                              marginBottom: "4px",
                                            }}
                                          >
                                            Eligibility & Warnings
                                          </div>
                                          <div
                                            style={{
                                              fontSize: "12px",
                                              color: "#374151",
                                              backgroundColor: "#fef2f2",
                                              padding: "10px",
                                              borderRadius: "4px",
                                              border: "1px solid #fecaca",
                                              lineHeight: "1.6",
                                            }}
                                          >
                                            {(medication.protocolMetadata
                                              .eligibility.min_age_years !==
                                              null ||
                                              medication.protocolMetadata
                                                .eligibility.max_age_years !==
                                                null) && (
                                              <div
                                                style={{ marginBottom: "6px" }}
                                              >
                                                <strong>Age Range:</strong>{" "}
                                                {medication.protocolMetadata
                                                  .eligibility.min_age_years ??
                                                  0}{" "}
                                                -{" "}
                                                {medication.protocolMetadata
                                                  .eligibility.max_age_years ??
                                                  "No max"}{" "}
                                                years
                                              </div>
                                            )}
                                            {medication.protocolMetadata
                                              .eligibility.pregnancy && (
                                              <div
                                                style={{ marginBottom: "6px" }}
                                              >
                                                <strong>Pregnancy:</strong>{" "}
                                                <span
                                                  style={{
                                                    color:
                                                      medication
                                                        .protocolMetadata
                                                        .eligibility
                                                        .pregnancy ===
                                                      "contraindicated"
                                                        ? "#dc2626"
                                                        : "#374151",
                                                    fontWeight:
                                                      medication
                                                        .protocolMetadata
                                                        .eligibility
                                                        .pregnancy ===
                                                      "contraindicated"
                                                        ? "600"
                                                        : "400",
                                                  }}
                                                >
                                                  {
                                                    medication.protocolMetadata
                                                      .eligibility.pregnancy
                                                  }
                                                </span>
                                                {medication.protocolMetadata
                                                  .eligibility
                                                  .pregnancy_notes && (
                                                  <div
                                                    style={{
                                                      marginLeft: "12px",
                                                      fontSize: "11px",
                                                      color: "#6b7280",
                                                      marginTop: "2px",
                                                    }}
                                                  >
                                                    {
                                                      medication
                                                        .protocolMetadata
                                                        .eligibility
                                                        .pregnancy_notes
                                                    }
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                            {medication.protocolMetadata
                                              .eligibility.breastfeeding && (
                                              <div>
                                                <strong>Breastfeeding:</strong>{" "}
                                                <span
                                                  style={{
                                                    color:
                                                      medication
                                                        .protocolMetadata
                                                        .eligibility
                                                        .breastfeeding ===
                                                      "contraindicated"
                                                        ? "#dc2626"
                                                        : "#374151",
                                                    fontWeight:
                                                      medication
                                                        .protocolMetadata
                                                        .eligibility
                                                        .breastfeeding ===
                                                      "contraindicated"
                                                        ? "600"
                                                        : "400",
                                                  }}
                                                >
                                                  {
                                                    medication.protocolMetadata
                                                      .eligibility.breastfeeding
                                                  }
                                                </span>
                                                {medication.protocolMetadata
                                                  .eligibility
                                                  .breastfeeding_notes && (
                                                  <div
                                                    style={{
                                                      marginLeft: "12px",
                                                      fontSize: "11px",
                                                      color: "#6b7280",
                                                      marginTop: "2px",
                                                    }}
                                                  >
                                                    {
                                                      medication
                                                        .protocolMetadata
                                                        .eligibility
                                                        .breastfeeding_notes
                                                    }
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                    {/* Side Effects */}
                                    {medication.protocolMetadata?.sideEffects &&
                                      (medication.protocolMetadata.sideEffects
                                        .common?.length > 0 ||
                                        medication.protocolMetadata.sideEffects
                                          .serious?.length > 0) && (
                                        <div style={{ marginBottom: "12px" }}>
                                          <div
                                            style={{
                                              fontSize: "11px",
                                              color: "#6b7280",
                                              fontWeight: "600",
                                              textTransform: "uppercase",
                                              marginBottom: "4px",
                                            }}
                                          >
                                            Side Effects
                                          </div>
                                          <div
                                            style={{
                                              fontSize: "12px",
                                              color: "#374151",
                                              backgroundColor: "#fff7ed",
                                              padding: "10px",
                                              borderRadius: "4px",
                                              border: "1px solid #fed7aa",
                                              lineHeight: "1.6",
                                            }}
                                          >
                                            {medication.protocolMetadata
                                              .sideEffects.common?.length >
                                              0 && (
                                              <div
                                                style={{ marginBottom: "8px" }}
                                              >
                                                <strong>Common:</strong>
                                                <ul
                                                  style={{
                                                    margin: "4px 0 0 0",
                                                    paddingLeft: "20px",
                                                  }}
                                                >
                                                  {medication.protocolMetadata.sideEffects.common.map(
                                                    (
                                                      effect: string,
                                                      i: number,
                                                    ) => (
                                                      <li
                                                        key={i}
                                                        style={{
                                                          marginBottom: "2px",
                                                        }}
                                                      >
                                                        {effect}
                                                      </li>
                                                    ),
                                                  )}
                                                </ul>
                                              </div>
                                            )}
                                            {medication.protocolMetadata
                                              .sideEffects.serious?.length >
                                              0 && (
                                              <div
                                                style={{ marginBottom: "8px" }}
                                              >
                                                <strong
                                                  style={{ color: "#dc2626" }}
                                                >
                                                  Serious:
                                                </strong>
                                                <ul
                                                  style={{
                                                    margin: "4px 0 0 0",
                                                    paddingLeft: "20px",
                                                  }}
                                                >
                                                  {medication.protocolMetadata.sideEffects.serious.map(
                                                    (
                                                      effect: string,
                                                      i: number,
                                                    ) => (
                                                      <li
                                                        key={i}
                                                        style={{
                                                          marginBottom: "2px",
                                                          color: "#991b1b",
                                                        }}
                                                      >
                                                        {effect}
                                                      </li>
                                                    ),
                                                  )}
                                                </ul>
                                              </div>
                                            )}
                                            {medication.protocolMetadata
                                              .sideEffects
                                              .when_to_seek_care && (
                                              <div>
                                                <strong>
                                                  When to Seek Care:
                                                </strong>{" "}
                                                {
                                                  medication.protocolMetadata
                                                    .sideEffects
                                                    .when_to_seek_care
                                                }
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                    {/* Drug Interactions */}
                                    {medication.protocolMetadata
                                      ?.drugInteractions &&
                                      (medication.protocolMetadata
                                        .drugInteractions.contraindicated
                                        ?.length > 0 ||
                                        medication.protocolMetadata
                                          .drugInteractions.monitoring_required
                                          ?.length > 0 ||
                                        medication.protocolMetadata
                                          .drugInteractions.reduced_efficacy
                                          ?.length > 0) && (
                                        <div style={{ marginBottom: "12px" }}>
                                          <div
                                            style={{
                                              fontSize: "11px",
                                              color: "#6b7280",
                                              fontWeight: "600",
                                              textTransform: "uppercase",
                                              marginBottom: "4px",
                                            }}
                                          >
                                            Drug Interactions
                                          </div>
                                          <div
                                            style={{
                                              fontSize: "12px",
                                              color: "#374151",
                                              backgroundColor: "#fdf4ff",
                                              padding: "10px",
                                              borderRadius: "4px",
                                              border: "1px solid #f5d0fe",
                                              lineHeight: "1.6",
                                            }}
                                          >
                                            {medication.protocolMetadata
                                              .drugInteractions.contraindicated
                                              ?.length > 0 && (
                                              <div
                                                style={{ marginBottom: "8px" }}
                                              >
                                                <strong
                                                  style={{ color: "#dc2626" }}
                                                >
                                                  Contraindicated:
                                                </strong>
                                                <ul
                                                  style={{
                                                    margin: "4px 0 0 0",
                                                    paddingLeft: "20px",
                                                  }}
                                                >
                                                  {medication.protocolMetadata.drugInteractions.contraindicated.map(
                                                    (
                                                      drug: string,
                                                      i: number,
                                                    ) => (
                                                      <li
                                                        key={i}
                                                        style={{
                                                          marginBottom: "2px",
                                                        }}
                                                      >
                                                        {drug}
                                                      </li>
                                                    ),
                                                  )}
                                                </ul>
                                              </div>
                                            )}
                                            {medication.protocolMetadata
                                              .drugInteractions.reduced_efficacy
                                              ?.length > 0 && (
                                              <div
                                                style={{ marginBottom: "8px" }}
                                              >
                                                <strong>
                                                  Reduced Efficacy:
                                                </strong>
                                                <ul
                                                  style={{
                                                    margin: "4px 0 0 0",
                                                    paddingLeft: "20px",
                                                  }}
                                                >
                                                  {medication.protocolMetadata.drugInteractions.reduced_efficacy.map(
                                                    (
                                                      drug: string,
                                                      i: number,
                                                    ) => (
                                                      <li
                                                        key={i}
                                                        style={{
                                                          marginBottom: "2px",
                                                        }}
                                                      >
                                                        {drug}
                                                      </li>
                                                    ),
                                                  )}
                                                </ul>
                                              </div>
                                            )}
                                            {medication.protocolMetadata
                                              .drugInteractions
                                              .monitoring_required?.length >
                                              0 && (
                                              <div>
                                                <strong>
                                                  Monitoring Required:
                                                </strong>
                                                <ul
                                                  style={{
                                                    margin: "4px 0 0 0",
                                                    paddingLeft: "20px",
                                                  }}
                                                >
                                                  {medication.protocolMetadata.drugInteractions.monitoring_required.map(
                                                    (
                                                      drug: string,
                                                      i: number,
                                                    ) => (
                                                      <li
                                                        key={i}
                                                        style={{
                                                          marginBottom: "2px",
                                                        }}
                                                      >
                                                        {drug}
                                                      </li>
                                                    ),
                                                  )}
                                                </ul>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                    {/* Storage Information */}
                                    {medication.protocolMetadata?.storage &&
                                      (medication.protocolMetadata.storage
                                        .temperature ||
                                        medication.protocolMetadata.storage
                                          .special_instructions) && (
                                        <div style={{ marginBottom: "12px" }}>
                                          <div
                                            style={{
                                              fontSize: "11px",
                                              color: "#6b7280",
                                              fontWeight: "600",
                                              textTransform: "uppercase",
                                              marginBottom: "4px",
                                            }}
                                          >
                                            Storage
                                          </div>
                                          <div
                                            style={{
                                              fontSize: "12px",
                                              color: "#374151",
                                              backgroundColor: "#ecfeff",
                                              padding: "10px",
                                              borderRadius: "4px",
                                              border: "1px solid #a5f3fc",
                                              lineHeight: "1.6",
                                            }}
                                          >
                                            {medication.protocolMetadata.storage
                                              .temperature && (
                                              <div
                                                style={{ marginBottom: "4px" }}
                                              >
                                                <strong>Temperature:</strong>{" "}
                                                {
                                                  medication.protocolMetadata
                                                    .storage.temperature
                                                }
                                              </div>
                                            )}
                                            {medication.protocolMetadata.storage
                                              .special_instructions && (
                                              <div>
                                                <strong>Instructions:</strong>{" "}
                                                {
                                                  medication.protocolMetadata
                                                    .storage
                                                    .special_instructions
                                                }
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                )}

                                {/* Pharmacist Note - Inside expandable */}
                                <div style={{ marginTop: "12px" }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      Pharmacist Note
                                    </div>
                                    {onSaveNote && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSaveNote(
                                            `manual-medication-${medication.protocol_id}`,
                                          );
                                        }}
                                        disabled={
                                          savingNotes[
                                            `manual-medication-${medication.protocol_id}`
                                          ]
                                        }
                                        style={{
                                          padding: "2px 8px",
                                          fontSize: "10px",
                                          fontWeight: "600",
                                          color: savingNotes[
                                            `manual-medication-${medication.protocol_id}`
                                          ]
                                            ? "#9ca3af"
                                            : "#3b82f6",
                                          backgroundColor: "transparent",
                                          border: "1px solid",
                                          borderColor: savingNotes[
                                            `manual-medication-${medication.protocol_id}`
                                          ]
                                            ? "#d1d5db"
                                            : "#3b82f6",
                                          borderRadius: "4px",
                                          cursor: savingNotes[
                                            `manual-medication-${medication.protocol_id}`
                                          ]
                                            ? "not-allowed"
                                            : "pointer",
                                          transition: "all 0.2s",
                                        }}
                                      >
                                        {savingNotes[
                                          `manual-medication-${medication.protocol_id}`
                                        ]
                                          ? "Saving Note..."
                                          : "Save Note"}
                                      </button>
                                    )}
                                  </div>
                                  <textarea
                                    placeholder="Add notes for this medication..."
                                    value={
                                      medicationNotes[
                                        `manual-medication-${medication.protocol_id}`
                                      ] ||
                                      medication.selectionDetails
                                        ?.pharmacistNote ||
                                      ""
                                    }
                                    onChange={(e) =>
                                      onNoteChange(
                                        `manual-medication-${medication.protocol_id}`,
                                        e.target.value,
                                      )
                                    }
                                    style={{
                                      width: "100%",
                                      minHeight: "60px",
                                      padding: "10px",
                                      fontSize: "12px",
                                      color: "#374151",
                                      backgroundColor: "#ffffff",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      resize: "vertical",
                                      fontFamily: "inherit",
                                      lineHeight: "1.5",
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}

                      {/* Current Session Manual Medication Selections */}
                      {manualSelections
                        .filter((s) => s.type === "medication")
                        .filter(
                          (s) =>
                            !(
                              aiRecommendations.recommendation
                                .pharmacistAddedMedications || []
                            ).some(
                              (m) => m.protocol_id === s.protocol.protocolId,
                            ),
                        )
                        .filter((s) => {
                          const selectionKey = `manual-medication-${s.protocol.protocolId}`;
                          return medicationSelections[selectionKey] !== false;
                        })
                        .map((selection, idx) => (
                          <div
                            key={`manual-medication-${idx}`}
                            style={{
                              backgroundColor: "#ffffff",
                              borderRadius: "10px",
                              border: "1px solid #e5e7eb",
                              borderLeft: "4px solid #eab308",
                              transition: "all 0.2s",
                              overflow: "hidden",
                            }}
                          >
                            {/* Collapsible Header */}
                            <div
                              onClick={() =>
                                toggleCard(`session-manual-medication-${idx}`)
                              }
                              style={{
                                padding: "12px 16px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: "8px",
                                cursor: "pointer",
                                backgroundColor: expandedCards[
                                  `session-manual-medication-${idx}`
                                ]
                                  ? "#f9fafb"
                                  : "#ffffff",
                                transition: "background-color 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#f9fafb";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  expandedCards[
                                    `session-manual-medication-${idx}`
                                  ]
                                    ? "#f9fafb"
                                    : "#ffffff";
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                  flex: 1,
                                }}
                              >
                                <svg
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    color: "#eab308",
                                    transform: expandedCards[
                                      `session-manual-medication-${idx}`
                                    ]
                                      ? "rotate(90deg)"
                                      : "rotate(0deg)",
                                    transition: "transform 0.2s",
                                    flexShrink: 0,
                                  }}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                                <span
                                  style={{
                                    fontWeight: "700",
                                    color: "#1f2937",
                                    fontSize: "15px",
                                  }}
                                >
                                  {selection.protocol.displayName}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <span
                                  style={{
                                    padding: "4px 10px",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    backgroundColor: "#fef3c7",
                                    color: "#92400e",
                                    border: "1px solid #fde047",
                                  }}
                                >
                                  MANUALLY ADDED
                                </span>
                                {/* Yes/No Selection Buttons */}
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    onClick={() =>
                                      handleManualSelectionChange(
                                        selection.protocol.protocolId,
                                        "medication",
                                        true,
                                      )
                                    }
                                    style={{
                                      padding: "4px 10px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      backgroundColor:
                                        medicationSelections[
                                          `manual-medication-${selection.protocol.protocolId}`
                                        ] === true
                                          ? "#dcfce7"
                                          : "white",
                                      color:
                                        medicationSelections[
                                          `manual-medication-${selection.protocol.protocolId}`
                                        ] === true
                                          ? "#166534"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleManualSelectionChange(
                                        selection.protocol.protocolId,
                                        "medication",
                                        false,
                                      )
                                    }
                                    style={{
                                      padding: "4px 10px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      backgroundColor:
                                        medicationSelections[
                                          `manual-medication-${selection.protocol.protocolId}`
                                        ] === false
                                          ? "#fee2e2"
                                          : "white",
                                      color:
                                        medicationSelections[
                                          `manual-medication-${selection.protocol.protocolId}`
                                        ] === false
                                          ? "#991b1b"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    No
                                  </button>
                                </div>
                                {/* Refills Dropdown */}
                                {onRefillInfoChange && onAdministrationSiteChange && (
                                  <RefillsDropdown
                                    medicationId={`manual-medication-${selection.protocol.protocolId}`}
                                    refillInfo={refillInfos?.[`manual-medication-${selection.protocol.protocolId}`] || ""}
                                    administrationSite={administrationSites?.[`manual-medication-${selection.protocol.protocolId}`] || null}
                                    quantity={quantities?.[`manual-medication-${selection.protocol.protocolId}`] || ""}
                                    onRefillInfoChange={onRefillInfoChange}
                                    onAdministrationSiteChange={onAdministrationSiteChange}
                                    onQuantityChange={onQuantityChange}
                                    onSaveDetails={onSaveDetails}
                                    note={medicationNotes[`manual-medication-${selection.protocol.protocolId}`] || selection.note || ""}
                                    isSaving={savingNotes[`manual-medication-${selection.protocol.protocolId}`]}
                                    isVaccine={false}
                                    isSelected={medicationSelections[`manual-medication-${selection.protocol.protocolId}`] === true}
                                  />
                                )}
                              </div>
                            </div>

                            {/* Expandable Content */}
                            {expandedCards[
                              `session-manual-medication-${idx}`
                            ] && (
                              <div style={{ padding: "0 16px 16px 16px" }}>
                                {/* Dosing & Route Info */}
                                <div
                                  className="grid grid-cols-1 sm:grid-cols-2"
                                  style={{
                                    marginBottom: "12px",
                                    padding: "10px",
                                    backgroundColor: "#f9fafb",
                                    borderRadius: "8px",
                                    gap: "8px",
                                  }}
                                >
                                  <div style={{ textAlign: "center" }}>
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Dosing
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#1f2937",
                                        fontWeight: "600",
                                        textAlign: "center",
                                      }}
                                    >
                                      {(selection.protocol as any).dosing?.adult
                                        ?.dose && (
                                        <div>
                                          Adult:{" "}
                                          {
                                            (selection.protocol as any).dosing
                                              .adult.dose
                                          }
                                        </div>
                                      )}
                                      {(selection.protocol as any).dosing
                                        ?.pediatric &&
                                        Array.isArray(
                                          (selection.protocol as any).dosing
                                            .pediatric,
                                        ) &&
                                        (selection.protocol as any).dosing
                                          .pediatric.length > 0 &&
                                        (
                                          selection.protocol as any
                                        ).dosing.pediatric.map(
                                          (ped: any, i: number) => (
                                            <div key={i}>
                                              {ped.age_range}: {ped.dose}
                                            </div>
                                          ),
                                        )}
                                      {!(selection.protocol as any).dosing
                                        ?.adult?.dose &&
                                        (!(selection.protocol as any).dosing
                                          ?.pediatric ||
                                          (selection.protocol as any).dosing
                                            .pediatric.length === 0) &&
                                        ((selection.protocol as any).doses ||
                                          "N/A")}
                                    </div>
                                  </div>
                                  <div style={{ textAlign: "center" }}>
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Route
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "13px",
                                        color: "#1f2937",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {(selection.protocol as any).dosing?.adult
                                        ?.route ||
                                        (selection.protocol as any).route}
                                    </div>
                                  </div>
                                </div>
                                {/* Timing Information */}
                                {(selection.protocol as any).timing &&
                                  ((selection.protocol as any).timing
                                    .minimum_days_before_travel ||
                                  (selection.protocol as any).timing
                                    .booster_interval ||
                                  (selection.protocol as any).timing
                                    .start_days_before ||
                                  (selection.protocol as any).timing
                                    .days_after_leaving ? (
                                    <div
                                      style={{
                                        marginBottom: "12px",
                                        padding: "10px",
                                        backgroundColor: "#fef3c7",
                                        borderRadius: "8px",
                                        border: "1px solid #fde047",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "10px",
                                          color: "#92400e",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "8px",
                                        }}
                                      >
                                        Timing Information
                                      </div>
                                      {(selection.protocol as any).timing
                                        .start_days_before && (
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#78350f",
                                            marginBottom: "6px",
                                          }}
                                        >
                                          <span style={{ fontWeight: "600" }}>
                                            Start:
                                          </span>{" "}
                                          {
                                            (selection.protocol as any).timing
                                              .start_days_before
                                          }{" "}
                                          days before travel
                                        </div>
                                      )}
                                      {(selection.protocol as any).timing
                                        .days_after_leaving && (
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#78350f",
                                            marginBottom: "6px",
                                          }}
                                        >
                                          <span style={{ fontWeight: "600" }}>
                                            Continue:
                                          </span>{" "}
                                          {
                                            (selection.protocol as any).timing
                                              .days_after_leaving
                                          }{" "}
                                          days after leaving
                                        </div>
                                      )}
                                      {(selection.protocol as any).timing
                                        .minimum_days_before_travel && (
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#78350f",
                                            marginBottom: "6px",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "6px",
                                          }}
                                        >
                                          <span style={{ fontWeight: "600" }}>
                                            Minimum days before travel:
                                          </span>
                                          <span style={{ fontWeight: "700" }}>
                                            {
                                              (selection.protocol as any).timing
                                                .minimum_days_before_travel
                                            }{" "}
                                            days
                                          </span>
                                        </div>
                                      )}
                                      {(selection.protocol as any).timing
                                        .booster_interval && (
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#78350f",
                                            lineHeight: "1.5",
                                          }}
                                        >
                                          <span style={{ fontWeight: "600" }}>
                                            Booster:
                                          </span>{" "}
                                          {
                                            (selection.protocol as any).timing
                                              .booster_interval
                                          }
                                        </div>
                                      )}
                                    </div>
                                  ) : null)}
                                {/* Brands */}
                                {(selection.protocol as any).brands &&
                                  (selection.protocol as any).brands.length >
                                    0 && (
                                    <div
                                      style={{
                                        marginBottom: "12px",
                                        padding: "10px",
                                        backgroundColor: "#f9fafb",
                                        borderRadius: "8px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "10px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Brands
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#1f2937",
                                        }}
                                      >
                                        {(
                                          selection.protocol as any
                                        ).brands.join(", ")}
                                      </div>
                                    </div>
                                  )}

                                {/* Pharmacist Note - Inside expandable */}
                                <div style={{ marginTop: "12px" }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      Pharmacist Note
                                    </div>
                                    {onSaveNote && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSaveNote(
                                            `manual-medication-${selection.protocol.protocolId}`,
                                          );
                                        }}
                                        disabled={
                                          savingNotes[
                                            `manual-medication-${selection.protocol.protocolId}`
                                          ]
                                        }
                                        style={{
                                          padding: "2px 8px",
                                          fontSize: "10px",
                                          fontWeight: "600",
                                          color: savingNotes[
                                            `manual-medication-${selection.protocol.protocolId}`
                                          ]
                                            ? "#9ca3af"
                                            : "#3b82f6",
                                          backgroundColor: "transparent",
                                          border: "1px solid",
                                          borderColor: savingNotes[
                                            `manual-medication-${selection.protocol.protocolId}`
                                          ]
                                            ? "#d1d5db"
                                            : "#3b82f6",
                                          borderRadius: "4px",
                                          cursor: savingNotes[
                                            `manual-medication-${selection.protocol.protocolId}`
                                          ]
                                            ? "not-allowed"
                                            : "pointer",
                                          transition: "all 0.2s",
                                        }}
                                      >
                                        {savingNotes[
                                          `manual-medication-${selection.protocol.protocolId}`
                                        ]
                                          ? "Saving Note..."
                                          : "Save Note"}
                                      </button>
                                    )}
                                  </div>
                                  <textarea
                                    placeholder="Add notes for this medication..."
                                    value={
                                      medicationNotes[
                                        `manual-medication-${selection.protocol.protocolId}`
                                      ] ||
                                      selection.note ||
                                      ""
                                    }
                                    onChange={(e) =>
                                      onNoteChange(
                                        `manual-medication-${selection.protocol.protocolId}`,
                                        e.target.value,
                                      )
                                    }
                                    style={{
                                      width: "100%",
                                      minHeight: "60px",
                                      padding: "10px",
                                      fontSize: "12px",
                                      color: "#374151",
                                      backgroundColor: "#ffffff",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      resize: "vertical",
                                      fontFamily: "inherit",
                                      lineHeight: "1.5",
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}

                      {/* AI Recommended Medications */}
                      {aiRecommendations.recommendation.medications
                        .filter(
                          (m) =>
                            m.status === "RECOMMENDED" ||
                            m.status === "CONDITIONAL",
                        )
                        .map((med, idx) => (
                          <div
                            key={idx}
                            style={{
                              backgroundColor: "#ffffff",
                              borderRadius: "10px",
                              border: "1px solid #e5e7eb",
                              borderLeft: "4px solid #f59e0b",
                              transition: "all 0.2s",
                              overflow: "hidden",
                            }}
                          >
                            {/* Collapsible Header */}
                            <div
                              onClick={() => toggleCard(`medication-${idx}`)}
                              style={{
                                padding: "12px 16px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: "8px",
                                cursor: "pointer",
                                backgroundColor: expandedCards[
                                  `medication-${idx}`
                                ]
                                  ? "#f9fafb"
                                  : "#ffffff",
                                transition: "background-color 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#f9fafb";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  expandedCards[`medication-${idx}`]
                                    ? "#f9fafb"
                                    : "#ffffff";
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                  flex: 1,
                                }}
                              >
                                <svg
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    color: "#f59e0b",
                                    transform: expandedCards[
                                      `medication-${idx}`
                                    ]
                                      ? "rotate(90deg)"
                                      : "rotate(0deg)",
                                    transition: "transform 0.2s",
                                    flexShrink: 0,
                                  }}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                                <span
                                  style={{
                                    fontWeight: "700",
                                    color: "#1f2937",
                                    fontSize: "15px",
                                  }}
                                >
                                  {med.medication_name}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <span
                                  style={{
                                    padding: "4px 10px",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    backgroundColor:
                                      med.status === "RECOMMENDED"
                                        ? "#dcfce7"
                                        : "#fef3c7",
                                    color:
                                      med.status === "RECOMMENDED"
                                        ? "#166534"
                                        : "#92400e",
                                    border: `1px solid ${
                                      med.status === "RECOMMENDED"
                                        ? "#86efac"
                                        : "#fde047"
                                    }`,
                                  }}
                                >
                                  {med.status}
                                </span>
                                {/* Yes/No Selection Buttons */}
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    onClick={() =>
                                      onSelectionChange(
                                        `ai-medication-${med.protocol_id}`,
                                        true,
                                      )
                                    }
                                    style={{
                                      padding: "4px 10px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      backgroundColor:
                                        medicationSelections[
                                          `ai-medication-${med.protocol_id}`
                                        ] === true
                                          ? "#dcfce7"
                                          : "white",
                                      color:
                                        medicationSelections[
                                          `ai-medication-${med.protocol_id}`
                                        ] === true
                                          ? "#166534"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    onClick={() =>
                                      onSelectionChange(
                                        `ai-medication-${med.protocol_id}`,
                                        false,
                                      )
                                    }
                                    style={{
                                      padding: "4px 10px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      backgroundColor:
                                        medicationSelections[
                                          `ai-medication-${med.protocol_id}`
                                        ] === false
                                          ? "#fee2e2"
                                          : "white",
                                      color:
                                        medicationSelections[
                                          `ai-medication-${med.protocol_id}`
                                        ] === false
                                          ? "#991b1b"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    No
                                  </button>
                                </div>
                                {/* Refills Dropdown */}
                                {onRefillInfoChange && onAdministrationSiteChange && (
                                  <RefillsDropdown
                                    medicationId={`ai-medication-${med.protocol_id}`}
                                    refillInfo={refillInfos?.[`ai-medication-${med.protocol_id}`] || med.selectionDetails?.refillInfo || ""}
                                    administrationSite={administrationSites?.[`ai-medication-${med.protocol_id}`] || med.selectionDetails?.administrationSite || null}
                                    quantity={quantities?.[`ai-medication-${med.protocol_id}`] || med.selectionDetails?.quantity || ""}
                                    onRefillInfoChange={onRefillInfoChange}
                                    onAdministrationSiteChange={onAdministrationSiteChange}
                                    onQuantityChange={onQuantityChange}
                                    onSaveDetails={onSaveDetails}
                                    note={medicationNotes[`ai-medication-${med.protocol_id}`] || med.selectionDetails?.pharmacistNote || ""}
                                    isSaving={savingNotes[`ai-medication-${med.protocol_id}`]}
                                    isVaccine={false}
                                    isSelected={medicationSelections[`ai-medication-${med.protocol_id}`] === true}
                                  />
                                )}
                              </div>
                            </div>

                            {/* Expandable Content */}
                            {expandedCards[`medication-${idx}`] && (
                              <div style={{ padding: "0 16px 16px 16px" }}>
                                {/* Quick Info Grid */}
                                <div
                                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
                                  style={{
                                    gap: "8px",
                                    marginBottom: "12px",
                                    padding: "10px",
                                    backgroundColor: "#f9fafb",
                                    borderRadius: "8px",
                                  }}
                                >
                                  <div style={{ textAlign: "center" }}>
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "2px",
                                      }}
                                    >
                                      Dose
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "13px",
                                        color: "#1f2937",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {med.dose}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      textAlign: "center",
                                      borderLeft: "1px solid #e5e7eb",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "2px",
                                      }}
                                    >
                                      Route
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "13px",
                                        color: "#1f2937",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {med.route}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      textAlign: "center",
                                      borderLeft: "1px solid #e5e7eb",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "2px",
                                      }}
                                    >
                                      Quantity
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "13px",
                                        color: "#1f2937",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {med.quantity}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      textAlign: "center",
                                      borderLeft: "1px solid #e5e7eb",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "2px",
                                      }}
                                    >
                                      Frequency
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "13px",
                                        color: "#1f2937",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {med.frequency || "As directed"}
                                    </div>
                                  </div>
                                </div>

                                {/* Timing Info */}
                                {med.timing && (
                                  <div
                                    style={{
                                      padding: "8px 12px",
                                      backgroundColor: "#eff6ff",
                                      borderRadius: "6px",
                                      marginBottom: "10px",
                                      fontSize: "12px",
                                      color: "#1e40af",
                                    }}
                                  >
                                    <strong>Timing:</strong> {med.timing}
                                  </div>
                                )}

                                {/* Flags Warning */}
                                {med.flags.length > 0 && (
                                  <div
                                    style={{
                                      padding: "10px 12px",
                                      backgroundColor: "#fef2f2",
                                      borderRadius: "8px",
                                      border: "1px solid #fecaca",
                                      display: "flex",
                                      alignItems: "flex-start",
                                      gap: "8px",
                                      marginBottom: "10px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#991b1b",
                                        fontWeight: "500",
                                      }}
                                    >
                                      {med.flags.join(" | ")}
                                    </div>
                                  </div>
                                )}

                                {/* Selection Status Badge - Always visible */}
                                {med.isSelected !== undefined && (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      marginBottom: "10px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "11px",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        color: "#6b7280",
                                      }}
                                    >
                                      Pharmacist Selection:
                                    </span>
                                    <span
                                      style={{
                                        padding: "2px 8px",
                                        borderRadius: "4px",
                                        fontSize: "12px",
                                        fontWeight: "700",
                                        backgroundColor: med.isSelected
                                          ? "#16a34a"
                                          : "#dc2626",
                                        color: "white",
                                      }}
                                    >
                                      {med.isSelected ? "Yes" : "No"}
                                    </span>
                                  </div>
                                )}

                                {/* More Details - Amber Expandable */}
                                <button
                                  onClick={() =>
                                    toggleDetails(`medication-${idx}`)
                                  }
                                  style={{
                                    width: "100%",
                                    padding: "10px 14px",
                                    backgroundColor: "#fef3c7",
                                    border: "1px solid #fcd34d",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: "#92400e",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "8px",
                                    transition: "all 0.2s",
                                    marginTop: "12px",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "#fde68a";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "#fef3c7";
                                  }}
                                >
                                  <span>More Details</span>
                                  <svg
                                    style={{
                                      width: "16px",
                                      height: "16px",
                                      transform: expandedDetails[
                                        `medication-${idx}`
                                      ]
                                        ? "rotate(180deg)"
                                        : "rotate(0deg)",
                                      transition: "transform 0.2s",
                                    }}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 9l-7 7-7-7"
                                    />
                                  </svg>
                                </button>

                                {/* Expandable Details */}
                                {expandedDetails[`medication-${idx}`] && (
                                  <div
                                    style={{
                                      marginTop: "8px",
                                      padding: "14px",
                                      backgroundColor: "#f9fafb",
                                      borderRadius: "8px",
                                      border: "1px solid #e5e7eb",
                                    }}
                                  >
                                    {med.reasoning && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Clinical Reasoning
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#eff6ff",
                                            padding: "10px",
                                            borderRadius: "4px",
                                            border: "1px solid #dbeafe",
                                            lineHeight: "1.5",
                                          }}
                                        >
                                          {med.reasoning}
                                        </div>
                                      </div>
                                    )}

                                    {med.quantity_reasoning && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Quantity Reasoning
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#fef3c7",
                                            padding: "10px",
                                            borderRadius: "4px",
                                            border: "1px solid #fde047",
                                            lineHeight: "1.5",
                                            fontStyle: "italic",
                                          }}
                                        >
                                          {med.quantity_reasoning}
                                        </div>
                                      </div>
                                    )}

                                    {med.counseling_points.length > 0 && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "6px",
                                          }}
                                        >
                                          Counseling Points
                                        </div>
                                        <ul
                                          style={{
                                            margin: 0,
                                            paddingLeft: "20px",
                                            fontSize: "12px",
                                            color: "#374151",
                                          }}
                                        >
                                          {med.counseling_points.map(
                                            (point, i) => (
                                              <li
                                                key={i}
                                                style={{
                                                  marginBottom: "4px",
                                                  lineHeight: "1.4",
                                                }}
                                              >
                                                {point}
                                              </li>
                                            ),
                                          )}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Pharmacist Note - Outside More Details but inside collapsible */}
                                <div style={{ marginTop: "12px" }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      Pharmacist Note
                                    </div>
                                    {onSaveNote && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSaveNote(
                                            `ai-medication-${med.protocol_id}`,
                                          );
                                        }}
                                        disabled={
                                          savingNotes[
                                            `ai-medication-${med.protocol_id}`
                                          ]
                                        }
                                        style={{
                                          padding: "2px 8px",
                                          fontSize: "10px",
                                          fontWeight: "600",
                                          color: savingNotes[
                                            `ai-medication-${med.protocol_id}`
                                          ]
                                            ? "#9ca3af"
                                            : "#3b82f6",
                                          backgroundColor: "transparent",
                                          border: "1px solid",
                                          borderColor: savingNotes[
                                            `ai-medication-${med.protocol_id}`
                                          ]
                                            ? "#d1d5db"
                                            : "#3b82f6",
                                          borderRadius: "4px",
                                          cursor: savingNotes[
                                            `ai-medication-${med.protocol_id}`
                                          ]
                                            ? "not-allowed"
                                            : "pointer",
                                          transition: "all 0.2s",
                                        }}
                                      >
                                        {savingNotes[
                                          `ai-medication-${med.protocol_id}`
                                        ]
                                          ? "Saving Note..."
                                          : "Save Note"}
                                      </button>
                                    )}
                                  </div>
                                  <textarea
                                    placeholder="Add notes for this medication..."
                                    value={
                                      medicationNotes[
                                        `ai-medication-${med.protocol_id}`
                                      ] ||
                                      med.selectionDetails?.pharmacistNote ||
                                      ""
                                    }
                                    onChange={(e) =>
                                      onNoteChange(
                                        `ai-medication-${med.protocol_id}`,
                                        e.target.value,
                                      )
                                    }
                                    style={{
                                      width: "100%",
                                      minHeight: "60px",
                                      padding: "10px",
                                      fontSize: "12px",
                                      color: "#374151",
                                      backgroundColor: "#ffffff",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      resize: "vertical",
                                      fontFamily: "inherit",
                                      lineHeight: "1.5",
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}

                      {/* NOT RECOMMENDED Medications - Displayed Inline */}
                      {(
                        aiRecommendations.recommendation.notRecommended
                          ?.medications || []
                      ).map((item, idx) => {
                        const displayName =
                          item.protocolMetadata?.displayName ||
                          item.medication_name;
                        const adultDose =
                          item.dose ||
                          item.protocolMetadata?.dosing?.adult?.dose;
                        const pediatricDosing =
                          item.protocolMetadata?.dosing?.pediatric;
                        const hasPediatricDosing =
                          Array.isArray(pediatricDosing) &&
                          pediatricDosing.length > 0;
                        // Get route from adult, or protocol metadata
                        const route =
                          item.route ||
                          item.protocolMetadata?.dosing?.adult?.route ||
                          item.protocolMetadata?.route;
                        const frequency =
                          item.protocolMetadata?.dosing?.adult?.frequency ||
                          (hasPediatricDosing && pediatricDosing[0]?.frequency);
                        return (
                          <div
                            key={`not-rec-medication-${idx}`}
                            style={{
                              backgroundColor: "#ffffff",
                              borderRadius: "10px",
                              border: "1px solid #fecaca",
                              borderLeft: "4px solid #dc2626",
                              transition: "all 0.2s",
                              overflow: "hidden",
                            }}
                          >
                            {/* Collapsible Header */}
                            <div
                              onClick={() =>
                                toggleCard(`not-rec-medication-${idx}`)
                              }
                              style={{
                                padding: "12px 16px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexWrap: "wrap",
                                gap: "8px",
                                cursor: "pointer",
                                backgroundColor: expandedCards[
                                  `not-rec-medication-${idx}`
                                ]
                                  ? "#f9fafb"
                                  : "#ffffff",
                                transition: "background-color 0.2s",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "#f9fafb";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  expandedCards[`not-rec-medication-${idx}`]
                                    ? "#f9fafb"
                                    : "#ffffff";
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                  flex: 1,
                                }}
                              >
                                <svg
                                  style={{
                                    width: "16px",
                                    height: "16px",
                                    color: "#dc2626",
                                    transform: expandedCards[
                                      `not-rec-medication-${idx}`
                                    ]
                                      ? "rotate(90deg)"
                                      : "rotate(0deg)",
                                    transition: "transform 0.2s",
                                    flexShrink: 0,
                                  }}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                                <span
                                  style={{
                                    fontWeight: "700",
                                    color: "#991b1b",
                                    fontSize: "15px",
                                  }}
                                >
                                  {displayName}
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <span
                                  style={{
                                    padding: "4px 10px",
                                    borderRadius: "6px",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    backgroundColor: "#fef2f2",
                                    color: "#dc2626",
                                    border: "1px solid #fecaca",
                                  }}
                                >
                                  NOT RECOMMENDED
                                </span>
                                {/* Yes/No Selection Buttons */}
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    onClick={() =>
                                      onSelectionChange(
                                        `not-rec-medication-${item.protocol_id}`,
                                        true,
                                      )
                                    }
                                    style={{
                                      padding: "4px 10px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      backgroundColor:
                                        medicationSelections[
                                          `not-rec-medication-${item.protocol_id}`
                                        ] === true
                                          ? "#dcfce7"
                                          : "white",
                                      color:
                                        medicationSelections[
                                          `not-rec-medication-${item.protocol_id}`
                                        ] === true
                                          ? "#166534"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    onClick={() =>
                                      onSelectionChange(
                                        `not-rec-medication-${item.protocol_id}`,
                                        false,
                                      )
                                    }
                                    style={{
                                      padding: "4px 10px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      backgroundColor:
                                        medicationSelections[
                                          `not-rec-medication-${item.protocol_id}`
                                        ] === false
                                          ? "#fee2e2"
                                          : "white",
                                      color:
                                        medicationSelections[
                                          `not-rec-medication-${item.protocol_id}`
                                        ] === false
                                          ? "#991b1b"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    No
                                  </button>
                                </div>
                                {/* Refills Dropdown */}
                                {onRefillInfoChange && onAdministrationSiteChange && (
                                  <RefillsDropdown
                                    medicationId={`not-rec-medication-${item.protocol_id}`}
                                    refillInfo={refillInfos?.[`not-rec-medication-${item.protocol_id}`] || ""}
                                    administrationSite={administrationSites?.[`not-rec-medication-${item.protocol_id}`] || null}
                                    quantity={quantities?.[`not-rec-medication-${item.protocol_id}`] || ""}
                                    onRefillInfoChange={onRefillInfoChange}
                                    onAdministrationSiteChange={onAdministrationSiteChange}
                                    onQuantityChange={onQuantityChange}
                                    onSaveDetails={onSaveDetails}
                                    note={medicationNotes[`not-rec-medication-${item.protocol_id}`] || ""}
                                    isSaving={savingNotes[`not-rec-medication-${item.protocol_id}`]}
                                    isVaccine={false}
                                    isSelected={medicationSelections[`not-rec-medication-${item.protocol_id}`] === true}
                                  />
                                )}
                              </div>
                            </div>

                            {/* Expandable Content */}
                            {expandedCards[`not-rec-medication-${idx}`] && (
                              <div
                                style={{
                                  padding: "0 16px 16px 16px",
                                }}
                              >
                                {/* Quick Info Grid */}
                                {(adultDose ||
                                  hasPediatricDosing ||
                                  route ||
                                  frequency) && (
                                  <div
                                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                                    style={{
                                      gap: "8px",
                                      marginBottom: "12px",
                                      padding: "10px",
                                      backgroundColor: "#f9fafb",
                                      borderRadius: "8px",
                                    }}
                                  >
                                    <div style={{ textAlign: "center" }}>
                                      <div
                                        style={{
                                          fontSize: "10px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "2px",
                                        }}
                                      >
                                        Dosing
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          color: "#1f2937",
                                          fontWeight: "600",
                                          textAlign: "left",
                                        }}
                                      >
                                        {adultDose && (
                                          <div>Adult: {adultDose}</div>
                                        )}
                                        {hasPediatricDosing &&
                                          pediatricDosing.map(
                                            (ped: any, i: number) => (
                                              <div key={i}>
                                                {ped.age_range}: {ped.dose}
                                              </div>
                                            ),
                                          )}
                                        {!adultDose &&
                                          !hasPediatricDosing &&
                                          "N/A"}
                                      </div>
                                    </div>
                                    {route && (
                                      <div style={{ textAlign: "center" }}>
                                        <div
                                          style={{
                                            fontSize: "10px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "2px",
                                          }}
                                        >
                                          Route
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "13px",
                                            color: "#1f2937",
                                            fontWeight: "600",
                                          }}
                                        >
                                          {route}
                                        </div>
                                      </div>
                                    )}
                                    {frequency && (
                                      <div style={{ textAlign: "center" }}>
                                        <div
                                          style={{
                                            fontSize: "10px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "2px",
                                          }}
                                        >
                                          Frequency
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "13px",
                                            color: "#1f2937",
                                            fontWeight: "600",
                                          }}
                                        >
                                          {frequency}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Reason */}
                                {item.reasoning && (
                                  <div style={{ marginBottom: "12px" }}>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Reason
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#374151",
                                        backgroundColor: "#fef2f2",
                                        padding: "10px",
                                        borderRadius: "4px",
                                        border: "1px solid #fecaca",
                                        lineHeight: "1.5",
                                      }}
                                    >
                                      {item.reasoning}
                                    </div>
                                  </div>
                                )}

                                {/* Therapy Class */}
                                {(item.therapy_class ||
                                  item.protocolMetadata?.therapyClass) && (
                                  <div style={{ marginBottom: "12px" }}>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Therapy Class
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#374151",
                                        backgroundColor: "#ffffff",
                                        padding: "8px 10px",
                                        borderRadius: "4px",
                                        border: "1px solid #e5e7eb",
                                      }}
                                    >
                                      {item.therapy_class ||
                                        item.protocolMetadata?.therapyClass}
                                    </div>
                                  </div>
                                )}

                                {/* Counseling Points */}
                                {item.protocolMetadata?.counselingPoints &&
                                  item.protocolMetadata.counselingPoints
                                    .length > 0 && (
                                    <div style={{ marginBottom: "12px" }}>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Counseling Points
                                      </div>
                                      <ul
                                        style={{
                                          fontSize: "12px",
                                          color: "#374151",
                                          backgroundColor: "#fffbeb",
                                          padding: "10px 10px 10px 28px",
                                          borderRadius: "4px",
                                          border: "1px solid #fde68a",
                                          lineHeight: "1.6",
                                          margin: 0,
                                        }}
                                      >
                                        {item.protocolMetadata.counselingPoints.map(
                                          (point, pointIdx) => (
                                            <li
                                              key={pointIdx}
                                              style={{
                                                marginBottom:
                                                  pointIdx <
                                                  item.protocolMetadata!
                                                    .counselingPoints!.length -
                                                    1
                                                    ? "4px"
                                                    : 0,
                                              }}
                                            >
                                              {point}
                                            </li>
                                          ),
                                        )}
                                      </ul>
                                    </div>
                                  )}

                                {/* Contraindications - Allergies */}
                                {item.protocolMetadata?.contraindications
                                  ?.allergies &&
                                  item.protocolMetadata.contraindications
                                    .allergies.length > 0 && (
                                    <div style={{ marginBottom: "12px" }}>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Allergy Contraindications
                                      </div>
                                      <ul
                                        style={{
                                          fontSize: "12px",
                                          color: "#374151",
                                          backgroundColor: "#fef2f2",
                                          padding: "10px 10px 10px 28px",
                                          borderRadius: "4px",
                                          border: "1px solid #fecaca",
                                          lineHeight: "1.6",
                                          margin: 0,
                                        }}
                                      >
                                        {item.protocolMetadata.contraindications.allergies.map(
                                          (allergy, allergyIdx) => (
                                            <li
                                              key={allergyIdx}
                                              style={{
                                                marginBottom:
                                                  allergyIdx <
                                                  item.protocolMetadata!
                                                    .contraindications!
                                                    .allergies!.length -
                                                    1
                                                    ? "4px"
                                                    : 0,
                                              }}
                                            >
                                              {allergy}
                                            </li>
                                          ),
                                        )}
                                      </ul>
                                    </div>
                                  )}

                                {/* Contraindications - Conditions */}
                                {item.protocolMetadata?.contraindications
                                  ?.conditions &&
                                  item.protocolMetadata.contraindications
                                    .conditions.length > 0 && (
                                    <div style={{ marginBottom: "12px" }}>
                                      <div
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                          textTransform: "uppercase",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Condition Contraindications
                                      </div>
                                      <ul
                                        style={{
                                          fontSize: "12px",
                                          color: "#374151",
                                          backgroundColor: "#fef2f2",
                                          padding: "10px 10px 10px 28px",
                                          borderRadius: "4px",
                                          border: "1px solid #fecaca",
                                          lineHeight: "1.6",
                                          margin: 0,
                                        }}
                                      >
                                        {item.protocolMetadata.contraindications.conditions.map(
                                          (condition, conditionIdx) => (
                                            <li
                                              key={conditionIdx}
                                              style={{
                                                marginBottom:
                                                  conditionIdx <
                                                  item.protocolMetadata!
                                                    .contraindications!
                                                    .conditions!.length -
                                                    1
                                                    ? "4px"
                                                    : 0,
                                              }}
                                            >
                                              {condition}
                                            </li>
                                          ),
                                        )}
                                      </ul>
                                    </div>
                                  )}

                                {/* Notes */}
                                {item.protocolMetadata?.notes && (
                                  <div style={{ marginBottom: "12px" }}>
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Notes
                                    </div>
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#374151",
                                        backgroundColor: "#f3f4f6",
                                        padding: "10px",
                                        borderRadius: "4px",
                                        border: "1px solid #e5e7eb",
                                        lineHeight: "1.5",
                                      }}
                                    >
                                      {item.protocolMetadata.notes}
                                    </div>
                                  </div>
                                )}

                                {/* More Details - Red Expandable */}
                                <button
                                  onClick={() =>
                                    toggleDetails(`not-rec-medication-${idx}`)
                                  }
                                  style={{
                                    width: "100%",
                                    padding: "10px 14px",
                                    backgroundColor: "#fee2e2",
                                    border: "1px solid #fecaca",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: "#991b1b",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "8px",
                                    transition: "all 0.2s",
                                    marginTop: "12px",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "#fecaca";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "#fee2e2";
                                  }}
                                >
                                  <span>More Details</span>
                                  <svg
                                    style={{
                                      width: "16px",
                                      height: "16px",
                                      transform: expandedDetails[
                                        `not-rec-medication-${idx}`
                                      ]
                                        ? "rotate(180deg)"
                                        : "rotate(0deg)",
                                      transition: "transform 0.2s",
                                    }}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 9l-7 7-7-7"
                                    />
                                  </svg>
                                </button>

                                {/* Expandable Details */}
                                {expandedDetails[
                                  `not-rec-medication-${idx}`
                                ] && (
                                  <div
                                    style={{
                                      marginTop: "8px",
                                      padding: "14px",
                                      backgroundColor: "#fef2f2",
                                      borderRadius: "8px",
                                      border: "1px solid #fecaca",
                                    }}
                                  >
                                    {/* Protocol ID */}
                                    {item.protocol_id && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Protocol ID
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#ffffff",
                                            padding: "8px 10px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                          }}
                                        >
                                          {item.protocol_id}
                                        </div>
                                      </div>
                                    )}

                                    {/* Quantity Info */}
                                    {item.quantity != null && item.quantity !== 0 && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Quantity
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#ffffff",
                                            padding: "8px 10px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                          }}
                                        >
                                          {item.quantity}
                                        </div>
                                      </div>
                                    )}

                                    {/* Protocol Timing */}
                                    {item.protocolMetadata?.timing && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Timing Details
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#ffffff",
                                            padding: "8px 10px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                            lineHeight: "1.6",
                                          }}
                                        >
                                          {(item.protocolMetadata.timing as any)
                                            .start_days_before && (
                                            <div>
                                              <strong>
                                                Start days before travel:
                                              </strong>{" "}
                                              {
                                                (
                                                  item.protocolMetadata
                                                    .timing as any
                                                ).start_days_before
                                              }
                                            </div>
                                          )}
                                          {(item.protocolMetadata.timing as any)
                                            .days_after_leaving && (
                                            <div>
                                              <strong>
                                                Days after leaving:
                                              </strong>{" "}
                                              {
                                                (
                                                  item.protocolMetadata
                                                    .timing as any
                                                ).days_after_leaving
                                              }
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Full Dosing Information */}
                                    {item.protocolMetadata?.dosing && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Full Dosing Information
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#ffffff",
                                            padding: "10px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                            lineHeight: "1.6",
                                          }}
                                        >
                                          {item.protocolMetadata.dosing
                                            .adult && (
                                            <div
                                              style={{ marginBottom: "8px" }}
                                            >
                                              <strong>Adult:</strong>
                                              <div
                                                style={{
                                                  marginLeft: "12px",
                                                  marginTop: "4px",
                                                }}
                                              >
                                                {item.protocolMetadata.dosing
                                                  .adult.dose && (
                                                  <div>
                                                    Dose:{" "}
                                                    {
                                                      item.protocolMetadata
                                                        .dosing.adult.dose
                                                    }
                                                  </div>
                                                )}
                                                {item.protocolMetadata.dosing
                                                  .adult.route && (
                                                  <div>
                                                    Route:{" "}
                                                    {
                                                      item.protocolMetadata
                                                        .dosing.adult.route
                                                    }
                                                  </div>
                                                )}
                                                {item.protocolMetadata.dosing
                                                  .adult.frequency && (
                                                  <div>
                                                    Frequency:{" "}
                                                    {
                                                      item.protocolMetadata
                                                        .dosing.adult.frequency
                                                    }
                                                  </div>
                                                )}
                                                {item.protocolMetadata.dosing
                                                  .adult.instructions && (
                                                  <div>
                                                    Instructions:{" "}
                                                    {
                                                      item.protocolMetadata
                                                        .dosing.adult
                                                        .instructions
                                                    }
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          )}
                                          {item.protocolMetadata.dosing
                                            .pediatric &&
                                            item.protocolMetadata.dosing
                                              .pediatric.length > 0 && (
                                              <div>
                                                <strong>Pediatric:</strong>
                                                {item.protocolMetadata.dosing.pediatric.map(
                                                  (ped, pedIdx) => (
                                                    <div
                                                      key={pedIdx}
                                                      style={{
                                                        marginLeft: "12px",
                                                        marginTop: "4px",
                                                        paddingLeft: "8px",
                                                        borderLeft:
                                                          "2px solid #e5e7eb",
                                                      }}
                                                    >
                                                      {ped.age_range && (
                                                        <div>
                                                          <strong>
                                                            {ped.age_range}:
                                                          </strong>
                                                        </div>
                                                      )}
                                                      {ped.dose && (
                                                        <div>
                                                          Dose: {ped.dose}
                                                        </div>
                                                      )}
                                                      {ped.frequency && (
                                                        <div>
                                                          Frequency:{" "}
                                                          {ped.frequency}
                                                        </div>
                                                      )}
                                                      {ped.instructions && (
                                                        <div>
                                                          Instructions:{" "}
                                                          {ped.instructions}
                                                        </div>
                                                      )}
                                                    </div>
                                                  ),
                                                )}
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    )}

                                    {/* Flags */}
                                    {item.flags && item.flags.length > 0 && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Flags
                                        </div>
                                        <div
                                          style={{
                                            display: "flex",
                                            gap: "6px",
                                            flexWrap: "wrap",
                                          }}
                                        >
                                          {item.flags.map((flag, flagIdx) => (
                                            <span
                                              key={flagIdx}
                                              style={{
                                                fontSize: "11px",
                                                padding: "4px 8px",
                                                backgroundColor: "#fef3c7",
                                                color: "#92400e",
                                                borderRadius: "4px",
                                                border: "1px solid #fde68a",
                                                fontWeight: "500",
                                              }}
                                            >
                                              {flag}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Status */}
                                    {item.status && (
                                      <div style={{ marginBottom: "12px" }}>
                                        <div
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                            textTransform: "uppercase",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Status
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            color: "#374151",
                                            backgroundColor: "#ffffff",
                                            padding: "8px 10px",
                                            borderRadius: "4px",
                                            border: "1px solid #e5e7eb",
                                          }}
                                        >
                                          {item.status}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Pharmacist Note - Outside More Details but inside collapsible */}
                                <div style={{ marginTop: "12px" }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "11px",
                                        color: "#6b7280",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                      }}
                                    >
                                      Pharmacist Note
                                    </div>
                                    {onSaveNote && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSaveNote(
                                            `not-rec-medication-${item.protocol_id}`,
                                          );
                                        }}
                                        disabled={
                                          savingNotes[
                                            `not-rec-medication-${item.protocol_id}`
                                          ]
                                        }
                                        style={{
                                          padding: "2px 8px",
                                          fontSize: "10px",
                                          fontWeight: "600",
                                          color: savingNotes[
                                            `not-rec-medication-${item.protocol_id}`
                                          ]
                                            ? "#9ca3af"
                                            : "#3b82f6",
                                          backgroundColor: "transparent",
                                          border: "1px solid",
                                          borderColor: savingNotes[
                                            `not-rec-medication-${item.protocol_id}`
                                          ]
                                            ? "#d1d5db"
                                            : "#3b82f6",
                                          borderRadius: "4px",
                                          cursor: savingNotes[
                                            `not-rec-medication-${item.protocol_id}`
                                          ]
                                            ? "not-allowed"
                                            : "pointer",
                                          transition: "all 0.2s",
                                        }}
                                      >
                                        {savingNotes[
                                          `not-rec-medication-${item.protocol_id}`
                                        ]
                                          ? "Saving Note..."
                                          : "Save Note"}
                                      </button>
                                    )}
                                  </div>
                                  <textarea
                                    placeholder="Add notes for this medication..."
                                    value={
                                      medicationNotes[
                                        `not-rec-medication-${item.protocol_id}`
                                      ] || ""
                                    }
                                    onChange={(e) =>
                                      onNoteChange(
                                        `not-rec-medication-${item.protocol_id}`,
                                        e.target.value,
                                      )
                                    }
                                    style={{
                                      width: "100%",
                                      minHeight: "60px",
                                      padding: "10px",
                                      fontSize: "12px",
                                      color: "#374151",
                                      backgroundColor: "#ffffff",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      resize: "vertical",
                                      fontFamily: "inherit",
                                      lineHeight: "1.5",
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Selection Modal - Separated Component */}
      <MedicationSelectionModal
        isOpen={showSelectionModal}
        onClose={() => setShowSelectionModal(false)}
        aiRecommendations={aiRecommendations}
        medicationSelections={medicationSelections}
        onSelectionChange={onSelectionChange}
        medicationNotes={medicationNotes}
        onNoteChange={onNoteChange}
        onSaveSelections={handleSaveSelectionsWithManual}
        savingSelections={savingSelections}
        manualSelections={manualSelections}
        onManualSelectionAdd={handleManualSelectionAdd}
        onManualSelectionRemove={handleManualSelectionRemove}
        pharmacistRecommendationNote={pharmacistRecommendationNote}
        onRecommendationNoteChange={onRecommendationNoteChange}
      />

      {/* Vaccine Administration Recording Modal (CAIR) */}
      {recordAdminTarget && (
        <RecordAdministrationModal
          isOpen={true}
          travelerId={travelerId}
          protocolKey={recordAdminTarget.protocolKey}
          vaccineName={recordAdminTarget.vaccineName}
          onClose={() => setRecordAdminTarget(null)}
        />
      )}
    </>
  );
}
